import React from "react";
import {
  Typography,
  Container,
  Paper,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Button,
  Chip,
  Stack,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Tooltip,
  Divider,
  Link
} from "@mui/material";
import { Refresh, CheckCircle, Cancel, DoneAll, ShoppingBag, NavigateBefore, NavigateNext, CalendarToday } from "@mui/icons-material";

import Client from "./components/Client";
import Config from "./components/ClientConfig";

const AUTO_REFRESH_MS = 60000;
const BUSINESS_TIMEZONE = "Europe/Bucharest";
const MIN_ORDER_DATE = "2000-01-01";
const FUTURE_ORDER_DAYS = 7;

function getBusinessDate(value = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: BUSINESS_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function shiftIsoDate(value, days) {
  const [year, month, day] = String(value).split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return [
    shifted.getUTCFullYear(),
    String(shifted.getUTCMonth() + 1).padStart(2, "0"),
    String(shifted.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

const isIsoDate = value => /^\d{4}-\d{2}-\d{2}$/.test(value) && shiftIsoDate(value, 0) === value;

function promisedDateIso(value) {
  if (isIsoDate(value)) return value;
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(String(value || ""));
  if (!match) return null;
  const normalized = `${match[3]}-${match[2]}-${match[1]}`;
  return isIsoDate(normalized) ? normalized : null;
}

function incidentWaitLabel(eligibleOn, serverNow) {
  const remainingMs = Date.parse(eligibleOn) - Date.parse(serverNow);
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return "";
  const minutes = Math.ceil(remainingMs / 60000);
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return hours ? `${hours}h ${remainder}m` : `${remainder}m`;
}

function destinationMapLink(destination) {
  const latitude = destination?.latitude;
  const longitude = destination?.longitude;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${latitude},${longitude}`)}`;
}

function orderListError(error, fallback) {
  if (error?.status === 401) return "Session expired. Sign in again.";
  if (error?.status === 403) return "You do not have permission to view these orders.";
  if (error?.status === 400) return "The selected order filters are invalid.";
  if (error?.status === 404) return "The order list is unavailable.";
  if (error?.status === 0) return "Connection lost while loading orders.";
  if (error?.status >= 500) return "The server could not load orders. Retry shortly.";
  return error?.responseJSON?.detail || error?.message || fallback;
}

function AutoRefreshStatus({ lastLoadedAt, busy, paused, onRefresh }) {
  const [nowMs, setNowMs] = React.useState(Date.now());
  const triggeredFor = React.useRef(null);

  React.useEffect(() => {
    const tick = () => {
      const now = Date.now();
      setNowMs(now);
      if (
        document.visibilityState === "visible" && lastLoadedAt && !busy && !paused
        && now - lastLoadedAt >= AUTO_REFRESH_MS && triggeredFor.current !== lastLoadedAt
      ) {
        triggeredFor.current = lastLoadedAt;
        onRefresh();
      }
    };
    const timer = setInterval(tick, 1000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [lastLoadedAt, busy, paused, onRefresh]);

  if (!lastLoadedAt) return null;
  const remainingSeconds = Math.max(0, Math.ceil((AUTO_REFRESH_MS - (nowMs - lastLoadedAt)) / 1000));
  const countdown = `${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, "0")}`;
  return (
    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
      {document.visibilityState === "visible"
        ? busy ? "Refreshing orders…" : paused ? "Auto-refresh paused during action" : `Next auto-refresh in ${countdown}`
        : "Auto-refresh paused while tab is hidden"}
    </Typography>
  );
}

const ORDER_STATUS_LABELS = {
  0: "Registered",
  1: "Complete",
  2: "Complete with errors",
  3: "Cancelled",
  4: "In sync",
  5: "Paid",
  6: "Accepted",
  7: "Ready",
  8: "Rejected",
  9: "Undefined",
};

const DELIVERY_PROGRESS_LABELS = {
  0: "Not applicable",
  1: "Registered",
  2: "Assigned",
  3: "Picked up",
  4: "On the way",
  5: "Delivered",
  6: "Failed",
  9: "Undefined",
};

const DELIVERY_PLATFORM_STATUS_LABELS = {
  0: "Created",
  1: "Rejected",
  2: "Scheduled",
  3: "Activated",
  4: "Accepted",
  5: "Waiting for pickup",
  6: "Picked",
  7: "Waiting for delivery",
  8: "Delivered",
  9: "Cancelled",
  10: "Returned",
};

const CANCELLABLE_PROVIDER_STATUSES = new Set([
  "NEW",
  "RECEIVED",
  "WAITING_FOR_TRANSPORT",
]);

const COURIER_CANCEL_REASONS = [
  { value: "DELIVERY_ETA_TOO_LONG", label: "Delivery ETA is too long" },
  { value: "MISTAKE_ERROR", label: "Courier requested by mistake" },
  { value: "REASON_UNKNOWN", label: "Other / unknown reason" },
];

function StatusChip({ status }) {
  const map = {
    1: { label: "Complete", color: "success" },
    5: { label: "Paid", color: "warning" },
    6: { label: "Accepted", color: "info" },
    // 7: { label: "Ready", color: "success" },
  };
  const m = map[status] || { label: status, color: "default" };
  return (
    <Chip
      size="small"
      label={m.label}
      color={m.color}
      variant={m.color === "default" ? "outlined" : "filled"}
    />
  );
}

function Actions({ row, onAccept, onCancel, onComplete, onDetails, disabled }) {
  const st = Number(row.status); // 5 = Paid, 6 = Accepted, 7 = Ready

  const showAccept = st === 5;   // Paid -> Accept
  const showCancel = st === 5;   // Paid -> Cancel
  const showReady  = st === 6;   // Accepted -> Ready
  // Ready (7) -> Details only

  return (
    <Stack direction="row" spacing={1}>
      <Tooltip title="Details">
        <span>
          <Button variant="outlined" size="small" disabled={disabled} onClick={() => onDetails(row)}>
            Details
          </Button>
        </span>
      </Tooltip>

      {showAccept && (
        <Tooltip title="Accept">
          <span>
            <Button
              variant="contained"
              size="small"
              color="success"
              disabled={disabled}
              startIcon={<CheckCircle />}
              onClick={() => onAccept(row)}
            >
              Accept
            </Button>
          </span>
        </Tooltip>
      )}

      {showCancel && (
        <Tooltip title="Cancel">
          <span>
            <Button
              variant="outlined"
              size="small"
              color="error"
              disabled={disabled}
              startIcon={<Cancel />}
              onClick={() => onCancel(row)}
            >
              Cancel
            </Button>
          </span>
        </Tooltip>
      )}

      {showReady && (
        <Tooltip title="Mark ready">
          <span>
            <Button
              variant="contained"
              size="small"
              color="primary"
              disabled={disabled}
              startIcon={<DoneAll />}
              onClick={() => onComplete(row)}
            >
              Ready
            </Button>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
}

export default class LocationOrdersPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client("none"),
      catalogsLoaded: false,
      // data
      orders: [],
      loading: false,
      ordersLoading: false,
      ordersLastLoadedAt: null,
      error: "",
      deliveryOrders: [],
      deliveryOrdersLoading: false,
      deliveryOrdersLastLoadedAt: null,
      deliveryOrdersError: "",
      deliveryLookup: { iref: "", loading: false, error: "" },
      // ui
      snack: { open: false, msg: "", sev: "success" },
      selectedStatus: 5, // 5 Paid | 6 Accepted | 7 Ready
      selectedDate: getBusinessDate(), // YYYY-MM-DD in Europe/Bucharest
      detailsDlg: { open: false, order: null },
      deliveryDetailsDlg: {
        open: false, order: null, loading: false, busy: false,
        snapshot: null, addressIref: "", recipientName: "", clientPhone: "", details: "",
        error: "", fieldErrors: {}, needsRefresh: false,
      },
      acceptDlg: { open: false, order: null, busy: false },
      cancelDlg: { open: false, order: null, reason: "" },
      courierDlg: {
        open: false,
        order: null,
        loading: false,
        busy: false,
        dispatch: null,
        incident: null,
        incidentActionError: "",
        unknownAction: null,
        closeConfirmed: false,
        replacementConfirmed: false,
        errorCode: "",
        error: "",
        notFound: false,
        confirmAction: null,
        cancelReason: "REASON_UNKNOWN",
      },
      // dictionaries
      productNameById: {},   // { iref: name }
      comboNameById: {},     // { iref: name }
      // views
      viewMode: "orders", // "orders" | "deliveryOrders" | "returns"
      // returns
      returnDlg: {
        open: false,
        step: 0,            // 0=Find Order, 1=Select Lines
        searchIref: "",
        loaded: false,
        sourceOrder: null,  // raw order snapshot loaded via reports / retrieve
        lines: [],          // flattened selectable lines from source order
        select: {},         // { key: qtyToReturn } (positive numbers; will send as negative)
        busy: false,
        err: "",
        payType: 1, // 1 Cash, 2 Bank Transfer
      },
    }
  }

  ordersRequestSequence = 0;
  deliveryOrdersRequestSequence = 0;
  courierRequestSequence = 0;
  deliveryDetailsRequestSequence = 0;
  deliveryDetailsPostInFlight = false;
  deliveryLookupSequence = 0;
  incidentPostInFlight = false;
  ordersRequest = null;
  deliveryOrdersRequest = null;

  // ---------- Small helpers ----------

  parseDate = (s) => {
    if (!s) return 0;
    const [d, t] = String(s).split(" ");
    if (!d) return 0;
    const parts = d.split("-");
    let y, m, day;
    if (parts[0] && parts[0].length === 4) [y, m, day] = parts.map(Number);
    else [day, m, y] = parts.map(Number);
    const [hh = 0, mm = 0, ss = 0] = (t || "00:00:00").split(":").map(Number);
    return new Date(y, m - 1, day, hh, mm, ss).getTime();
  };

  // Initial dictionary fill (may be partial; we'll also resolve missing ones lazily)
  loadDictionaries = async () => {
    const { client } = this.state;

    // TIP: if some names still show as IDs, remove the status filter so you fetch ALL items.
    const pr = await Promise.resolve(client.getData(
      Config.API_URL_PRODUCT + 'list_products/',
      { /* status: 2, */ orderBy_name: true }
    )) || {};
    const pList = Array.isArray(pr) ? pr : pr.products || pr.results || pr.items || [];
    const productNameById = {};
    pList.forEach((p) => {
      const id = String(p?.iref ?? p?.id ?? "");
      if (id) productNameById[id] = p?.name || p?.label || id;
    });

    const cr = await Promise.resolve(client.getData(
      Config.API_URL_COMBO + 'list_combos/',
      { /* status: 2, */ orderBy_name: true }
    )) || {};
    const cList = Array.isArray(cr)
      ? cr
      : (Array.isArray(cr.productCombos) && cr.productCombos.length ? cr.productCombos
         : (Array.isArray(cr.combos) && cr.combos.length ? cr.combos
            : (cr.results || cr.items || [])));

    const comboNameById = {};
    cList.forEach((c) => {
      const id = String(c?.iref ?? c?.id ?? "");
      if (id) comboNameById[id] = c?.name || c?.label || id;
    });

    if (!this.unmounted) this.setState({ productNameById, comboNameById, catalogsLoaded: true });
  };

  // ----------------- Data -----------------
  loadOrders = async () => {
    const { client, selectedStatus, selectedDate } = this.state;
    const requestId = ++this.ordersRequestSequence;
    const isCurrent = () => requestId === this.ordersRequestSequence
      && selectedStatus === this.state.selectedStatus && selectedDate === this.state.selectedDate;
    this.ordersRequest?.abort?.();
    this.setState({ ordersLoading: true, error: "" });
    try {
      const request = client.getDataAsync(
        Config.API_URL_ORDERS,
        { status: selectedStatus, promisedDate: selectedDate }
      );
      this.ordersRequest = request;
      const resp = await request;
      if (!isCurrent()) return;
      const list = Array.isArray(resp) ? resp : resp?.orders || resp?.results || resp?.items;
      if (!Array.isArray(list)) throw new Error("Invalid orders response.");

      const mapped = list
        .map(this.mapOrderRow)
        .sort((a, b) => this.parseDate(b.created) - this.parseDate(a.created));

      this.setState({ orders: mapped, ordersLoading: false, ordersLastLoadedAt: Date.now() });
    } catch (e) {
      if (isCurrent()) {
        this.setState({
          orders: [],
          ordersLoading: false,
          ordersLastLoadedAt: Date.now(),
          error: orderListError(e, "Failed to load orders")
        });
      }
    } finally {
      if (requestId === this.ordersRequestSequence) {
        this.ordersRequest = null;
        if (!isCurrent()) this.setState({ ordersLoading: false });
      }
    }
  };

  loadDeliveryOrders = async () => {
    const { client, selectedDate } = this.state;
    const requestId = ++this.deliveryOrdersRequestSequence;
    const isCurrent = () => requestId === this.deliveryOrdersRequestSequence
      && selectedDate === this.state.selectedDate;
    this.deliveryOrdersRequest?.abort?.();
    this.setState({ deliveryOrdersLoading: true, deliveryOrdersError: "" });

    try {
      const request = client.getDataAsync(
        `${Config.API_URL_ORDERS}completeOrders/`,
        { promisedDate: selectedDate }
      );
      this.deliveryOrdersRequest = request;
      const resp = await request;
      if (!isCurrent()) return;

      if (!Array.isArray(resp)) {
        throw new Error("Invalid delivery orders response.");
      }

      const deliveryOrders = resp
        .map((order) => ({
          ...this.mapOrderRow(order),
          number: order.iref,
        }))
        .sort((a, b) => this.parseDate(b.created) - this.parseDate(a.created));

      this.setState({
        deliveryOrders,
        deliveryOrdersLoading: false,
        deliveryOrdersLastLoadedAt: Date.now()
      });
    } catch (e) {
      if (isCurrent()) {
        this.setState({
          deliveryOrders: [],
          deliveryOrdersLoading: false,
          deliveryOrdersLastLoadedAt: Date.now(),
          deliveryOrdersError: orderListError(e, "Failed to load delivery orders"),
        });
      }
    } finally {
      if (requestId === this.deliveryOrdersRequestSequence) {
        this.deliveryOrdersRequest = null;
        if (!isCurrent()) this.setState({ deliveryOrdersLoading: false });
      }
    }
  };

  getProductIref = (p) => String(
    p?.ProductPrice?.product ?? p?.ProductCompany?.product ?? p?.product ?? ""
  );
  getComboIref = (c) => String(
    c?.ComboPrice?.productCombo ?? c?.ComboCompany?.productCombo ?? c?.productCombo ?? ""
  );
  
  getPerson = (o) => o?.client?.user?.person || null;

  getClientName = (o) => {
    if (o && Object.prototype.hasOwnProperty.call(o, "contactName")) {
      return o.contactName || "-";
    }
    const user = o?.client?.user || {};
    const person = user?.person || {};

    const firstName = person.firstName || user.first_name || "";
    const lastName = person.lastName || user.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || user.email || "-";
  };

  getDeliveryPhone = (delivery, order) => {
    const value = order && Object.prototype.hasOwnProperty.call(order, "contactPhone")
      ? order.contactPhone : delivery?.clientPhone;
    const phone = String(value || "").trim();
    return phone || "Missing from delivery order — Glovo courier request will be rejected.";
  };

  getDeliveryInfo = (o) => {
    const delivery = Array.isArray(o?.OD_order) && o.OD_order.length > 0
      ? o.OD_order[0]
      : null;

    if (!delivery) {
      return { delivery: null, address: null };
    }

    const person = this.getPerson(o);
    const deliveryAddressIref = String(delivery.address || "");

    const additionalAddress = (person?.additionaladdress_person || []).find(
      (x) => deliveryAddressIref && String(x?.address?.iref || "") === deliveryAddressIref
    );

    const address =
      additionalAddress?.address ||
      (deliveryAddressIref && String(person?.address?.iref || "") === deliveryAddressIref
        ? person.address
        : null);

    return { delivery, address };
  };

  getDeliveryTypeLabel = (type) => {
    const map = {
      0: "PickUp",
      1: "Delivery",
      2: "Transport",
      3: "Glovo",
      4: "Tazz",
      5: "Bolt",
      6: "Bringo",
      7: "Food Panda",
      8: "Takeaway",
      9: "Undefined",
    };
    return map[Number(type)] || "Undefined";
  };

  getPickupCode = (o, delivery) => {
    const existing = String(delivery?.pickupCode || "").trim();
    if (existing && existing !== "0") {
      return existing;
    }
    const code = String(o?.code || "").trim();
    return code ? code.slice(-4) : "-";
  };

  formatAddress = (address) => {
    if (!address) return "-";

    const city =
      typeof address.city === "object"
        ? address.city?.name
        : address.city || "";

    const parts = [
      address.name,
      [address.street, address.number].filter(Boolean).join(" "),
      address.entrance ? `Entrance: ${address.entrance}` : "",
      address.floor ? `Floor: ${address.floor}` : "",
      address.local ? `Apartment: ${address.local}` : "",
      address.complement,
      city,
      address.postalCode ? `Postal code: ${address.postalCode}` : "",
    ].filter(Boolean);

    return parts.length ? parts.join(", ") : "-";
  };

  InfoLine = ({ label, value }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {value || "-"}
      </Typography>
    </Box>
  );
  
  mapOrderRow = (o) => {
    const products = [
      ...(o.orderProductEntry_order || []),
      ...(o.orderProductCompanyEntry_order || []),
    ];
    const combos = [
      ...(o.orderComboEntry_order || []),
      ...(o.orderComboCompanyEntry_order || []),
    ];
    const prodQty = products.reduce((s, p) => s + Number(p.quantity || 0), 0);
    const comboQty = combos.reduce((s, c) => s + Number(c.quantity || 0), 0);
    const promoProdQty = (o.orderProductPromotionEntry_order || []).reduce((s, pp) => s + Number(pp.quantity || 0), 0);
    const promoComboQty = (o.orderComboPromotionEntry_order || []).reduce((s, cp) => s + Number(cp.quantity || 0), 0);

    const items = [];
    if (prodQty > 0)  items.push({ key: `agg_products_${o.iref}`, label: `product × ${prodQty}`, color: "default" });
    if (comboQty > 0) items.push({ key: `agg_combos_${o.iref}`,   label: `combo × ${comboQty}`,   color: "info" });
    if (promoProdQty > 0)  items.push({ key: `agg_promo_prod_${o.iref}`,  label: `product promo × ${promoProdQty}`, color: "warning" });
    if (promoComboQty > 0) items.push({ key: `agg_promo_combo_${o.iref}`, label: `combo promo × ${promoComboQty}`,  color: "warning" });

    return {
      iref: o.iref,
      number: o.docNumber || o.iref,
      created: o.createdOn,
      status: o.status,
      deliveryStatus: o.deliveryStatus,
      notes: o.description,
      customerName: o && Object.prototype.hasOwnProperty.call(o, "contactName")
        ? (o.contactName || "-")
        : (o?.client?.user?.first_name
          ? `${o.client.user.first_name} ${o.client.user.last_name}`
          : o?.client?.user?.email || "-"),
      locationName: o?.pos?.name || "-",
      items,
      itemsCount:
        products.length +
        combos.length +
        (o.orderProductPromotionEntry_order?.length || 0) +
        (o.orderComboPromotionEntry_order?.length || 0),
      total: Number(o.totalAmount ?? 0),
      raw: o,
    };
  };

  // ----------------- UI Actions -----------------
  handleStatusClick = (st) => {
    this.deliveryLookupSequence += 1;
    this.setState((state) => ({
      selectedStatus: st,
      viewMode: "orders",
      deliveryLookup: { ...state.deliveryLookup, loading: false },
    }), this.loadOrders);
  };
  setSelectedDate = (value) => {
    if (!isIsoDate(value) || value < MIN_ORDER_DATE
        || value > shiftIsoDate(getBusinessDate(), FUTURE_ORDER_DAYS)
        || value === this.state.selectedDate) return;
    this.deliveryLookupSequence += 1;
    this.setState((state) => ({
      selectedDate: value,
      deliveryLookup: { ...state.deliveryLookup, loading: false },
    }), this.refreshActiveView);
  };
  handleDateChange = (e) => this.setSelectedDate(e.target.value);
  moveDate = (days) => this.setSelectedDate(shiftIsoDate(this.state.selectedDate, days));
  openDeliveryOrdersView = () => this.setState(
    { viewMode: "deliveryOrders" },
    this.loadDeliveryOrders
  );
  findDeliveryOrder = async () => {
    const iref = String(this.state.deliveryLookup.iref || "").trim();
    const requestId = ++this.deliveryLookupSequence;
    if (!/^[1-9]\d*$/.test(iref)) {
      this.setState((state) => ({
        deliveryLookup: { ...state.deliveryLookup, loading: false, error: "Enter a valid order number." },
      }));
      return;
    }
    this.setState((state) => ({
      deliveryLookup: { ...state.deliveryLookup, loading: true, error: "" },
    }));
    try {
      const response = await this.state.client.getDataAsync(Config.API_URL_ORDERS, { iref });
      if (requestId !== this.deliveryLookupSequence || this.unmounted) return;
      if (!Array.isArray(response?.orders)) throw new Error("Invalid order lookup response.");
      const order = response.orders.find((item) => String(item?.iref) === iref);
      if (!order || Number(order.status) !== 1 || Number(order.deliveryType) !== 1) {
        throw new Error("Completed delivery order not found or access is unavailable.");
      }
      const promisedDate = promisedDateIso(order.promisedDate);
      if (!promisedDate) throw new Error("Order promised date is unavailable.");
      this.setState((state) => requestId === this.deliveryLookupSequence && !this.unmounted ? ({
        selectedDate: promisedDate,
        viewMode: "deliveryOrders",
        deliveryLookup: { ...state.deliveryLookup, loading: false, error: "" },
      }) : null, () => {
        if (requestId !== this.deliveryLookupSequence || this.unmounted) return;
        this.loadDeliveryOrders();
        this.openCourierDialog({ ...this.mapOrderRow(order), number: order.iref });
      });
    } catch (error) {
      if (requestId !== this.deliveryLookupSequence || this.unmounted) return;
      this.setState((state) => requestId === this.deliveryLookupSequence && !this.unmounted ? ({
        deliveryLookup: {
          ...state.deliveryLookup,
          loading: false,
          error: orderListError(error, "Could not find delivery order."),
        },
      }) : null);
    }
  };
  refreshActiveView = () => {
    if (this.state.viewMode === "deliveryOrders") {
      return this.loadDeliveryOrders();
    }
    if (this.state.viewMode === "orders") {
      return this.loadOrders();
    }
    return undefined;
  };

  getDeliveryProgressLabel = (value) => {
    const status = Number(value);
    return Object.prototype.hasOwnProperty.call(DELIVERY_PROGRESS_LABELS, status)
      ? DELIVERY_PROGRESS_LABELS[status]
      : `Unknown (${value ?? "-"})`;
  };

  getOrderStatusLabel = (value) => {
    const status = Number(value);
    return Object.prototype.hasOwnProperty.call(ORDER_STATUS_LABELS, status)
      ? ORDER_STATUS_LABELS[status]
      : `Unknown (${value ?? "-"})`;
  };

  getDeliveryPlatformStatusLabel = (value) => {
    const status = Number(value);
    return Object.prototype.hasOwnProperty.call(DELIVERY_PLATFORM_STATUS_LABELS, status)
      ? DELIVERY_PLATFORM_STATUS_LABELS[status]
      : `Unknown (${value ?? "-"})`;
  };

  formatCourierDateTime = (value) => {
    if (!value) return "Not reported";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "Not reported";
    return new Intl.DateTimeFormat("ro-RO", {
      timeZone: "Europe/Bucharest",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(parsed);
  };

  getCourierPhoneLink = (value) => {
    const phone = String(value || "").trim();
    return /^\+[1-9][0-9]{6,14}$/.test(phone) ? `tel:${phone}` : "";
  };

  getSafeTrackingLink = (value) => {
    try {
      const parsed = new URL(String(value || ""));
      const hostname = parsed.hostname.toLowerCase();
      const glovoHost = hostname === "glovoapp.com" || hostname.endsWith(".glovoapp.com");
      if (
        parsed.protocol !== "https:"
        || parsed.username
        || parsed.password
        || (parsed.port && parsed.port !== "443")
        || !glovoHost
      ) {
        return "";
      }
      return parsed.toString();
    } catch (error) {
      return "";
    }
  };

  courierErrorPayload = (error) => (
    error?.responseJSON || error?.response?.data || error?.response || error || {}
  );

  courierErrorMessage = (payload, fallback) => (
    payload?.message
    || payload?.detail
    || this.extractApiError(payload)
    || fallback
  );

  openDeliveryDetails = (order) => {
    if (!order || this.deliveryDetailsPostInFlight) return;
    this.deliveryDetailsRequestSequence += 1;
    this.setState({
      deliveryDetailsDlg: {
        open: true, order, loading: true, busy: false,
        snapshot: null, addressIref: "", recipientName: "", clientPhone: "", details: "",
        error: "", fieldErrors: {}, needsRefresh: false,
      },
    }, this.loadDeliveryDetails);
  };

  closeDeliveryDetails = () => {
    if (this.deliveryDetailsPostInFlight) return;
    this.deliveryDetailsRequestSequence += 1;
    this.setState((state) => ({
      deliveryDetailsDlg: { ...state.deliveryDetailsDlg, open: false, order: null },
    }));
  };

  validDeliveryDetailsResponse = (response, orderIref) => Boolean(
    response && !response.errorCode
    && String(response.orderIref) === String(orderIref)
    && typeof response.updateKey === "string" && response.updateKey
    && Object.prototype.hasOwnProperty.call(response, "dispatchIref")
    && typeof response.canEdit === "boolean"
    && Array.isArray(response.addresses)
    && Object.prototype.hasOwnProperty.call(response, "delivery")
  );

  loadDeliveryDetails = async () => {
    const { deliveryDetailsDlg, client } = this.state;
    if (!deliveryDetailsDlg.open || !deliveryDetailsDlg.order || this.deliveryDetailsPostInFlight) return;
    const orderIref = deliveryDetailsDlg.order.iref;
    const requestId = ++this.deliveryDetailsRequestSequence;
    const isCurrent = () => !this.unmounted && requestId === this.deliveryDetailsRequestSequence
      && this.state.deliveryDetailsDlg.open
      && this.state.deliveryDetailsDlg.order?.iref === orderIref;
    this.setState((state) => ({
      deliveryDetailsDlg: { ...state.deliveryDetailsDlg, loading: true, error: "", fieldErrors: {} },
    }));
    try {
      const response = await client.getDataAsync(
        `${Config.API_URL_ORDERS}${encodeURIComponent(orderIref)}/delivery_details/`, {}
      );
      if (!isCurrent()) return;
      if (!this.validDeliveryDetailsResponse(response, orderIref)) {
        throw new Error(this.courierErrorMessage(response, "Could not load delivery details."));
      }
      this.setState((state) => ({
        deliveryDetailsDlg: {
          ...state.deliveryDetailsDlg, loading: false, snapshot: response, needsRefresh: false,
          addressIref: String(response.delivery?.addressIref || ""),
          recipientName: response.delivery?.recipientName || "",
          clientPhone: response.delivery?.clientPhone || "",
          details: response.delivery?.details || "",
        },
      }));
    } catch (error) {
      if (!isCurrent()) return;
      this.setState((state) => ({
        deliveryDetailsDlg: {
          ...state.deliveryDetailsDlg, loading: false, snapshot: null,
          error: this.courierErrorMessage(this.courierErrorPayload(error), "Could not load delivery details."),
        },
      }));
    }
  };

  changeDeliveryDetail = (field, value) => this.setState((state) => ({
    deliveryDetailsDlg: {
      ...state.deliveryDetailsDlg, [field]: value,
      fieldErrors: { ...state.deliveryDetailsDlg.fieldErrors, [field]: "" },
    },
  }));

  saveDeliveryDetails = async () => {
    const { deliveryDetailsDlg: dialog, client } = this.state;
    if (this.deliveryDetailsPostInFlight || !dialog.open || dialog.loading || dialog.busy
      || dialog.needsRefresh || !dialog.snapshot?.canEdit) return;
    let phone = dialog.clientPhone.replace(/[\s().-]/g, "");
    if (phone.startsWith("00")) phone = `+${phone.slice(2)}`;
    else if (phone.startsWith("0")) phone = `+40${phone.slice(1)}`;
    else if (/^[237][0-9]{8}$/.test(phone)) phone = `+40${phone}`;
    const fieldErrors = {};
    if (!dialog.snapshot.addresses.some((address) => String(address.iref) === dialog.addressIref)) {
      fieldErrors.addressIref = "Select a saved delivery address.";
    }
    if (!dialog.recipientName.trim()) fieldErrors.recipientName = "Enter the recipient name.";
    const validPhone = phone.startsWith("+40")
      ? /^\+40[237][0-9]{8}$/.test(phone)
      : /^\+[1-9][0-9]{6,11}$/.test(phone);
    if (!validPhone) {
      fieldErrors.clientPhone = "Enter a valid Romanian phone number or an international number with country code (maximum 13 characters including +).";
    }
    if (Object.keys(fieldErrors).length) {
      this.setState((state) => ({ deliveryDetailsDlg: { ...state.deliveryDetailsDlg, fieldErrors } }));
      return;
    }
    this.deliveryDetailsPostInFlight = true;
    const orderIref = dialog.order.iref;
    const requestId = ++this.deliveryDetailsRequestSequence;
    const isCurrent = () => !this.unmounted && requestId === this.deliveryDetailsRequestSequence
      && this.state.deliveryDetailsDlg.open
      && this.state.deliveryDetailsDlg.order?.iref === orderIref;
    this.setState((state) => ({
      deliveryDetailsDlg: { ...state.deliveryDetailsDlg, busy: true, error: "", fieldErrors: {} },
    }));
    try {
      const response = await client.requestJsonAsync(
        `${Config.API_URL_ORDERS}${encodeURIComponent(orderIref)}/delivery_details/`, "POST", {
          updateKey: dialog.snapshot.updateKey,
          dispatchIref: dialog.snapshot.dispatchIref,
          addressIref: Number(dialog.addressIref),
          recipientName: dialog.recipientName.trim(), clientPhone: phone, details: dialog.details,
        }
      );
      if (!isCurrent()) return;
      if (!this.validDeliveryDetailsResponse(response, orderIref) || !response.delivery) {
        throw new Error(this.courierErrorMessage(response, "Delivery details save was not confirmed. Refresh before retrying."));
      }
      this.setState((state) => ({
        deliveryDetailsDlg: { ...state.deliveryDetailsDlg, open: false, busy: false, order: null },
        detailsDlg: state.detailsDlg.order?.iref === orderIref
          ? { open: false, order: null } : state.detailsDlg,
      }));
      this.toast("Delivery details saved");
      await this.refreshActiveView();
      if (!this.unmounted && this.state.courierDlg.open && this.state.courierDlg.order?.iref === orderIref) {
        await this.loadCourierState();
      }
    } catch (error) {
      if (!isCurrent()) return;
      const payload = this.courierErrorPayload(error);
      const httpStatus = error?.status ?? error?.response?.status;
      const needsRefresh = !httpStatus || httpStatus === 409 || httpStatus >= 500;
      const message = this.courierErrorMessage(payload, "Could not save delivery details.");
      this.setState((state) => ({
        deliveryDetailsDlg: {
          ...state.deliveryDetailsDlg, busy: false, needsRefresh,
          error: needsRefresh ? `${message} Refresh delivery details before trying again.` : message,
          fieldErrors: payload?.errorCode === "DELIVERY_INVALID_PHONE" ? { clientPhone: message } : {},
        },
      }));
    } finally {
      this.deliveryDetailsPostInFlight = false;
    }
  };

  openCourierDialog = (order) => {
    this.courierRequestSequence += 1;
    this.setState({
      courierDlg: {
        open: true,
        order,
        loading: true,
        busy: false,
        dispatch: null,
        incident: null,
        incidentActionError: "",
        unknownAction: null,
        closeConfirmed: false,
        replacementConfirmed: false,
        errorCode: "",
        error: "",
        notFound: false,
        confirmAction: null,
        cancelReason: "REASON_UNKNOWN",
      },
    }, () => this.loadCourierState(order));
  };

  closeCourierDialog = () => {
    if (this.state.courierDlg.busy) return;
    this.courierRequestSequence += 1;
    this.setState({
      courierDlg: {
        open: false,
        order: null,
        loading: false,
        busy: false,
        dispatch: null,
        incident: null,
        incidentActionError: "",
        unknownAction: null,
        closeConfirmed: false,
        replacementConfirmed: false,
        errorCode: "",
        error: "",
        notFound: false,
        confirmAction: null,
        cancelReason: "REASON_UNKNOWN",
      },
    });
  };

  loadCourierState = async (order = this.state.courierDlg.order) => {
    if (!order) return;
    const orderIref = order.iref;
    const requestId = ++this.courierRequestSequence;
    this.setState((state) => ({
      courierDlg: {
        ...state.courierDlg,
        loading: true,
        errorCode: "",
        error: "",
        notFound: false,
        confirmAction: null,
      },
    }));

    try {
      const response = await this.state.client.getDataAsync(
        `${Config.API_URL_ORDERS}${encodeURIComponent(orderIref)}/glovo_get/`,
        {}
      );
      if (!response || typeof response !== "object" || !response.dispatch) {
        throw new Error("Invalid managed courier response.");
      }
      this.setState((state) => {
        if (
          requestId !== this.courierRequestSequence
          || !state.courierDlg.open
          || state.courierDlg.order?.iref !== orderIref
        ) return null;
        const unknownAction = state.courierDlg.unknownAction;
        const confirmed = unknownAction && (
          unknownAction.action === "close"
            ? response.incident?.closedUndelivered === true
            : Number(response.dispatch.generation) > unknownAction.generation
        );
        return {
          courierDlg: {
            ...state.courierDlg,
            loading: false,
            dispatch: response.dispatch,
            incident: response.incident || null,
            unknownAction: confirmed ? null : unknownAction,
            incidentActionError: unknownAction && !confirmed
              ? state.courierDlg.incidentActionError : "",
            errorCode: response.errorCode || "",
            error: response.errorCode ? this.courierErrorMessage(response, "Courier status requires attention.") : "",
            notFound: false,
          },
        };
      });
    } catch (error) {
      const payload = this.courierErrorPayload(error);
      const notFound = payload?.errorCode === "MANAGED_DISPATCH_NOT_FOUND";
      this.setState((state) => {
        if (
          requestId !== this.courierRequestSequence
          || !state.courierDlg.open
          || state.courierDlg.order?.iref !== orderIref
        ) return null;
        const unknownAction = state.courierDlg.unknownAction;
        const confirmed = unknownAction && (
          unknownAction.action === "close"
            ? payload?.incident?.closedUndelivered === true
            : Number(payload?.dispatch?.generation) > unknownAction.generation
        );
        return {
          courierDlg: {
            ...state.courierDlg,
            loading: false,
            dispatch: payload?.dispatch || null,
            incident: payload?.incident || null,
            unknownAction: confirmed ? null : unknownAction,
            incidentActionError: unknownAction && !confirmed
              ? state.courierDlg.incidentActionError : "",
            errorCode: notFound ? "" : (payload?.errorCode || "COURIER_STATUS_FAILED"),
            error: notFound
              ? ""
              : this.courierErrorMessage(payload, "Could not load managed courier status."),
            notFound,
          },
        };
      });
    }
  };

  canCreateCourier = () => {
    const { courierDlg } = this.state;
    if (!courierDlg.order || courierDlg.loading || courierDlg.busy || courierDlg.incidentActionError) return false;
    if (courierDlg.incident || courierDlg.unknownAction) return false;
    const dispatch = courierDlg.dispatch;
    if (dispatch?.canRetryAfterCorrection === true
      && (!courierDlg.errorCode || courierDlg.errorCode === dispatch.errorCode)
      && (!courierDlg.error || courierDlg.errorCode === dispatch.errorCode)) return true;
    if (courierDlg.errorCode || courierDlg.error) return false;
    if (courierDlg.notFound) {
      const deliveryStatus = Number(
        courierDlg.order.raw?.deliveryStatus ?? courierDlg.order.deliveryStatus
      );
      return !courierDlg.dispatch && deliveryStatus === 1;
    }
    return Boolean(
      dispatch
      && dispatch.status === "CANCELLED"
      && dispatch.providerStatus === "CANCELLED"
      && !dispatch.errorCode
      && !dispatch.pickedUpOn
    );
  };

  canCancelCourier = () => {
    const { courierDlg } = this.state;
    if (!courierDlg.order || courierDlg.loading || courierDlg.busy || courierDlg.errorCode
      || courierDlg.error || courierDlg.incidentActionError || courierDlg.unknownAction) return false;
    const dispatch = courierDlg.dispatch;
    return Boolean(
      dispatch
      && dispatch.status === "ACTIVE"
      && CANCELLABLE_PROVIDER_STATUSES.has(dispatch.providerStatus)
      && !dispatch.errorCode
      && !dispatch.pickedUpOn
    );
  };

  canCloseCourierIncident = () => {
    const { courierDlg } = this.state;
    return Boolean(
      !courierDlg.loading && !courierDlg.busy && !courierDlg.unknownAction
      && Number.isInteger(Number(courierDlg.dispatch?.iref))
      && Number(courierDlg.dispatch.iref) > 0
      && courierDlg.incident?.closedUndelivered === false
      && courierDlg.incident?.canCloseUndelivered === true
    );
  };

  canRequestIncidentReplacement = () => {
    const { courierDlg } = this.state;
    return Boolean(
      !courierDlg.loading && !courierDlg.busy && !courierDlg.unknownAction
      && Number.isInteger(Number(courierDlg.dispatch?.iref))
      && Number(courierDlg.dispatch.iref) > 0
      && courierDlg.incident?.canRequestReplacement === true
      && courierDlg.incident?.historicalEvidenceConflict !== true
      && courierDlg.incident?.destinationFingerprint
      && courierDlg.incident?.destination?.address
      && courierDlg.incident?.destination?.phone
      && courierDlg.incident?.destination?.recipientName
      && destinationMapLink(courierDlg.incident?.destination)
      && Number.isInteger(Number(courierDlg.dispatch.generation))
      && Number(courierDlg.dispatch.generation) > 0
    );
  };

  requestIncidentConfirmation = (action) => {
    if (action === "close" && !this.canCloseCourierIncident()) return;
    if (action === "replace" && !this.canRequestIncidentReplacement()) return;
    this.setState((state) => ({
      courierDlg: {
        ...state.courierDlg,
        confirmAction: action,
        closeConfirmed: false,
        replacementConfirmed: false,
        incidentActionError: "",
      },
    }));
  };

  confirmIncidentAction = async () => {
    const { client, courierDlg } = this.state;
    const { order, dispatch, incident, confirmAction } = courierDlg;
    if (!order || !dispatch || this.incidentPostInFlight || courierDlg.busy) return;
    if (confirmAction === "close" && (!this.canCloseCourierIncident() || !courierDlg.closeConfirmed)) return;
    if (confirmAction === "replace" && (!this.canRequestIncidentReplacement() || !courierDlg.replacementConfirmed)) return;
    if (!["close", "replace"].includes(confirmAction)) return;

    const previousGeneration = Number(dispatch.generation);
    const actionUrl = confirmAction === "close"
      ? "glovo_close_undelivered" : "glovo_request_replacement";
    const body = confirmAction === "close"
      ? { dispatchIref: Number(dispatch.iref), confirmUndelivered: true }
      : {
        closedDispatchIref: Number(dispatch.iref),
        confirmReadyForRedelivery: true,
        destinationFingerprint: incident.destinationFingerprint,
      };
    this.incidentPostInFlight = true;
    this.courierRequestSequence += 1;
    this.setState((state) => ({
      courierDlg: { ...state.courierDlg, busy: true, incidentActionError: "" },
    }));
    try {
      const response = await client.requestJsonAsync(
        `${Config.API_URL_ORDERS}${encodeURIComponent(order.iref)}/${actionUrl}/`,
        "POST",
        body
      );
      const confirmed = response?.dispatch && response?.incident
        && Number(response.dispatch.orderIref) === Number(order.iref) && (
        confirmAction === "close"
          ? response.incident.closedUndelivered === true
            && Number(response.dispatch.iref) === Number(dispatch.iref)
          : response.incident.closedUndelivered === false
            && Number(response.dispatch.generation) > previousGeneration
      );
      if (!confirmed) throw new Error("Incident response was not confirmed. Refresh courier status before taking another action.");
      this.setState((state) => ({
        courierDlg: {
          ...state.courierDlg,
          busy: false,
          dispatch: response.dispatch,
          incident: response.incident,
          errorCode: confirmAction === "replace" ? "" : state.courierDlg.errorCode,
          error: confirmAction === "replace" ? "" : state.courierDlg.error,
          incidentActionError: "",
          unknownAction: null,
          confirmAction: null,
          closeConfirmed: false,
          replacementConfirmed: false,
        },
      }));
      this.toast(confirmAction === "close"
        ? "Delivery marked undelivered"
        : "Replacement Glovo courier requested for newly prepared products");
      this.loadDeliveryOrders();
    } catch (error) {
      const payload = this.courierErrorPayload(error);
      const uncertain = !Number.isInteger(error?.status)
        || error.status === 0 || error.status >= 500;
      const message = uncertain
        ? "Result uncertain. Refresh courier status before taking another action; do not retry this request."
        : this.courierErrorMessage(payload, "Delivery incident action was rejected. Refresh courier status.");
      this.setState((state) => ({
        courierDlg: {
          ...state.courierDlg,
          busy: false,
          dispatch: payload?.dispatch || state.courierDlg.dispatch,
          incident: uncertain
            ? payload?.incident || (Number(payload?.dispatch?.iref) === Number(dispatch.iref)
              ? state.courierDlg.incident : null)
            : null,
          incidentActionError: message,
          unknownAction: uncertain ? { action: confirmAction, generation: previousGeneration } : null,
          confirmAction: null,
          closeConfirmed: false,
          replacementConfirmed: false,
        },
      }));
      this.toast(message, "error");
    } finally {
      this.incidentPostInFlight = false;
    }
  };

  requestCourierConfirmation = (action) => {
    if (action === "create" && !this.canCreateCourier()) return;
    if (action === "cancel" && !this.canCancelCourier()) return;
    this.setState((state) => ({
      courierDlg: { ...state.courierDlg, confirmAction: action, errorCode: "", error: "" },
    }));
  };

  confirmCourierAction = async () => {
    const { client, courierDlg } = this.state;
    const { order, confirmAction, cancelReason } = courierDlg;
    if (!order || courierDlg.busy || !["create", "cancel"].includes(confirmAction)) return;
    if (confirmAction === "create" && !this.canCreateCourier()) return;
    if (confirmAction === "cancel" && !this.canCancelCourier()) return;

    this.setState((state) => ({
      courierDlg: { ...state.courierDlg, busy: true, errorCode: "", error: "" },
    }));

    const actionUrl = confirmAction === "create" ? "glovo_create" : "glovo_cancel";
    const body = confirmAction === "create" ? {} : { reason: cancelReason };
    const unconfirmedMessage = `Courier ${confirmAction === "cancel" ? "cancellation" : "request"} was not confirmed. Refresh courier status before trying again.`;
    try {
      const response = await Promise.resolve(client.pushData(
        `${Config.API_URL_ORDERS}${encodeURIComponent(order.iref)}/${actionUrl}/`,
        body
      ));
      if (!response || typeof response !== "object") {
        throw new Error(unconfirmedMessage);
      }

      const failed = Boolean(
        response.errorCode
        || String(response.status || "").toLowerCase() === "error"
      );
      const dispatch = response.dispatch;
      const expectedGeneration = confirmAction === "cancel"
        ? Number(courierDlg.dispatch?.generation)
        : Number(courierDlg.dispatch?.generation || 0)
          + (courierDlg.dispatch?.canRetryAfterCorrection === true ? 0 : 1);
      if (!failed && !(
        dispatch && String(dispatch.orderIref) === String(order.iref)
        && Number.isInteger(dispatch.generation) && dispatch.generation > 0
        && dispatch.generation === expectedGeneration
        && typeof dispatch.providerOrderId === "string" && dispatch.providerOrderId.trim()
        && !dispatch.errorCode
        && (confirmAction === "create"
          ? ["ACTIVE", "DELIVERED", "CANCELLED"].includes(dispatch.status)
            && typeof dispatch.providerStatus === "string" && dispatch.providerStatus.trim()
          : dispatch.status === "CANCELLED" && dispatch.providerStatus === "CANCELLED"
            && dispatch.providerOrderId === courierDlg.dispatch?.providerOrderId)
      )) throw new Error(unconfirmedMessage);
      const message = failed
        ? this.courierErrorMessage(response, "Managed courier operation failed.")
        : "";
      this.setState((state) => ({
        courierDlg: {
          ...state.courierDlg,
          busy: false,
          dispatch: response.dispatch || state.courierDlg.dispatch,
          errorCode: failed ? (response.errorCode || response.code || "COURIER_OPERATION_FAILED") : "",
          error: message,
          notFound: false,
          confirmAction: null,
        },
      }));

      if (failed) {
        this.toast(message, "error");
      } else {
        this.toast(confirmAction === "create" ? "Glovo courier requested" : "Glovo courier cancelled");
      }
    } catch (error) {
      const payload = this.courierErrorPayload(error);
      const message = this.courierErrorMessage(payload, "Managed courier operation failed.");
      this.setState((state) => ({
        courierDlg: {
          ...state.courierDlg,
          busy: false,
          dispatch: payload?.dispatch || state.courierDlg.dispatch,
          errorCode: payload?.errorCode || "COURIER_OPERATION_FAILED",
          error: message,
          confirmAction: null,
        },
      }));
      this.toast(message, "error");
    }
  };

  toast = (msg, sev = "success") => this.setState({ snack: { open: true, msg, sev } });

  onAccept = (o) => this.setState({
    acceptDlg: { open: true, order: o, busy: false },
  });

  confirmAccept = async () => {
    const { client, acceptDlg } = this.state;
    const order = acceptDlg.order;
    if (!order || acceptDlg.busy) return;

    this.setState({ acceptDlg: { ...acceptDlg, busy: true } });
    try {
      const resp = await client.pushData(
        `${Config.API_URL_ORDERS}acceptOrder/?iref=${encodeURIComponent(order.iref)}`,
        {}
      );

      if (resp?.status === "Error" || resp?.status === "error") {
        throw new Error(this.extractApiError(resp) || `Error: ${resp.code}`);
      }

      this.setState({ acceptDlg: { open: false, order: null, busy: false } });
      this.toast(`Order #${order.number} accepted`);
      await this.loadOrders();

    } catch (e) {
      this.setState((state) => ({
        acceptDlg: { ...state.acceptDlg, busy: false },
      }));
      this.toast(e.message || "Failed to accept order", "error");
    }
  };

  onCancel = (o) => this.setState({ cancelDlg: { open: true, order: o, reason: "" } });
  
  confirmCancel = async () => {
    const { client, cancelDlg } = this.state;
    try {
      const resp = await client.pushData(
        `${Config.API_URL_ORDERS}cancelOrder/?iref=${encodeURIComponent(cancelDlg.order.iref)}`,
        { reason: cancelDlg.reason }
      );

      if (resp?.status === "Error") {
        throw new Error(resp.message || `Error: ${resp.code}`);
      }

      this.toast(`Order #${cancelDlg.order.number} cancelled`);
      this.setState({ cancelDlg: { open: false, order: null, reason: "" } });
      await this.loadOrders();
    } catch (e) {
      this.toast(this.extractApiError(e) || e.message || "Failed to cancel order", "error");
    }
  };

  onComplete = (o) => {
    try {
      const resp = this.state.client.pushData(
        `${Config.API_URL_ORDERS}completeOrder/?iref=${encodeURIComponent(o.iref)}`,
        { deliveredOn: new Date().toISOString() }
      );
      if (String(resp?.status || "").toLowerCase() !== "ok") {
        throw new Error(this.extractApiError(resp) || resp?.message || "Order completion was not confirmed. Refresh the order before trying again.");
      }
      this.toast(`Order #${o.number} ready`);
      this.loadOrders();
    } catch (e) { this.toast(e.message, "error"); }
  };

  // ----- View switching (single-view page) -----
  openReturnView = () => {
    this.deliveryLookupSequence += 1;
    this.setState({
      viewMode: "returns",
      returnDlg: {
        open: true,          // inline panel visible
        step: 0,
        searchIref: "",
        loaded: false,
        sourceOrder: null,
        lines: [],
        select: {},
        busy: false,
        err: "",
        payType: 1,
      }
    });
  };

  backToOrdersView = () => {
    this.setState({
      viewMode: "orders",
      returnDlg: {
        open: false,
        step: 0,
        searchIref: "",
        loaded: false,
        sourceOrder: null,
        lines: [],
        select: {},
        busy: false,
        err: "",
      }
    }, () => this.loadOrders()); // optional immediate refresh
  };

  loadReturnSourceByIref = async () => {
    this.setState((s) => ({
      returnDlg: { ...s.returnDlg, busy: true, err: "", success: null },
    }));

    const safeDone = (patch) =>
      this.setState((s) => ({
        returnDlg: { ...s.returnDlg, busy: false, ...(patch || {}) },
      }));

    try {
      const irefTxt = String(this.state.returnDlg.searchIref || "").trim();
      if (!/^\d+$/.test(irefTxt)) {
        throw new Error("Order iref must be numeric.");
      }
      const iref = parseInt(irefTxt, 10);

      const { client, productNameById, comboNameById } = this.state;

      let data = null;
      try {
        data = await Promise.resolve(
          client.getData(Config.API_URL_ORDERS, { iref, status: [1, 2] })
        );
      } catch (_) {}

      let r =
        (data && (data.result || data.order || data.item)) ||
        (data && (data.orders || data.results || data.items)) ||
        data;

      if (Array.isArray(r)) {
        r = r.find((x) => String(x?.iref) === String(iref)) || r[0];
      }
      if (r && (r.result || r.order || r.item)) {
        r = r.result || r.order || r.item;
      }

      if (!r || typeof r !== "object") {
        throw new Error("Order not found or order is not complete.");
      }

      const allLines = [];

      const pushLine = (ln, type, labelPrefix = "") => {
        const product =
          ln?.ProductPrice?.product ??
          ln?.ProductPromotion?.product ??
          ln?.ProductCompany?.product ??
          ln?.product ??
          null;

        const combo =
          ln?.ComboPrice?.productCombo ??
          ln?.ComboPromotion?.productCombo ??
          ln?.ComboCompany?.productCombo ??
          ln?.productCombo ??
          null;

        const parentQty = Math.abs(Number(ln.quantity || 0)) || 1;
        let comboChildren = [];

        if (type === "combo" && Array.isArray(ln.OrderComboProductsEntry_OrderComboEntry)) {
          comboChildren = ln.OrderComboProductsEntry_OrderComboEntry.map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            unitQty: Number(ch.quantity || 0) / parentQty,
            unitVal: Number(ch.value || 0) / parentQty,
            unitVat: Number(ch.vat || 0) / parentQty,
          }));
        }

        if (
          type === "comboPromo" &&
          Array.isArray(ln.OrderComboPromotionProductsEntry_OrderComboPromotionEntry)
        ) {
          comboChildren = ln.OrderComboPromotionProductsEntry_OrderComboPromotionEntry.map(
            (ch) => ({
              product: ch.product,
              priceType: ch.priceType,
              priceList: ch.priceList ?? null,
              unitQty: Number(ch.quantity || 0) / parentQty,
              unitVal: Number(ch.value || 0) / parentQty,
              unitVat: Number(ch.vat || 0) / parentQty,
            })
          );
        }

        if (
          type === "comboCompany" &&
          Array.isArray(ln.OrderComboCompanyProductsEntry_OrderComboCompanyEntry)
        ) {
          comboChildren = ln.OrderComboCompanyProductsEntry_OrderComboCompanyEntry.map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            unitQty: Number(ch.quantity || 0) / parentQty,
            unitVal: Number(ch.value || 0) / parentQty,
            unitVat: Number(ch.vat || 0) / parentQty,
          }));
        }

        const dictName =
          (product && productNameById?.[String(product)]) ||
          (combo && comboNameById?.[String(combo)]) ||
          null;

        const label =
          labelPrefix +
          (ln.name ||
            ln.productName ||
            ln.comboName ||
            dictName ||
            (product ? `Product ${product}` : combo ? `Combo ${combo}` : "Line"));

        allLines.push({
          key: `${type}_${product || combo}_${allLines.length}`,
          label,
          type,
          product,
          combo,
          productCompany: ln?.ProductCompany?.company ?? null,
          comboCompany: ln?.ComboCompany?.company ?? null,
          qty: Number(ln.quantity || 0),
          value: Number(ln.value || 0),
          discountValue: Number(ln.discountValue || 0),
          vat: Number(ln.vat || 0),
          priceList:
            ln?.ProductPrice?.priceList ??
            ln?.ComboPrice?.priceList ??
            ln?.ProductCompany?.priceList ??
            ln?.ComboCompany?.priceList ??
            null,
          promotionList:
            ln?.ProductPromotion?.promotionList ??
            ln?.ComboPromotion?.promotionList ??
            null,
          comboChildren,
        });
      };

      (r.orderProductEntry_order || []).forEach((l) => pushLine(l, "product", ""));
      (r.orderComboEntry_order || []).forEach((l) => pushLine(l, "combo", ""));
      (r.orderProductPromotionEntry_order || []).forEach((l) =>
        pushLine(l, "prodPromo", "Promo ")
      );
      (r.orderComboPromotionEntry_order || []).forEach((l) =>
        pushLine(l, "comboPromo", "Promo ")
      );
      (r.orderProductCompanyEntry_order || []).forEach((l) =>
        pushLine(l, "productCompany", "")
      );

      (r.orderComboCompanyEntry_order || []).forEach((l) =>
        pushLine(l, "comboCompany", "")
      );

      if (!allLines.length) {
        throw new Error("This order has no lines to return.");
      }

      const sourceOrder = {
        iref: String(r.iref ?? iref),
        code: r.code || "",
        type: String(r.type ?? 0),
        clientIref: String(r?.client?.iref ?? ""),
        pos: r?.pos ?? null,
        date: r.documentDate || "",
        valueWVat: r.totalAmount ?? 0,
        valueWoVat: r.totalAmountWithoutVAT ?? 0,
        discountValue: r.discountValue ?? 0,
        discountValueWithoutVAT: r.discountValueWithoutVAT ?? 0,
      };

      let existing = [];
      try {
        existing = await this.findExistingReturns({
          code: sourceOrder.code,
          iref: sourceOrder.iref,
          date: sourceOrder.date,
        });
      } catch (_) {}

      if (existing.length > 0) {
        safeDone({
          loaded: true,
          step: 0,
          sourceOrder,
          lines: [],
          select: {},
          existingReturns: existing,
          err: "This order already has a return registered.",
        });
        return;
      }

      safeDone({
        loaded: true,
        step: 1,
        sourceOrder,
        lines: allLines,
        select: {},
        existingReturns: [],
        err: "",
      });

      try {
        const existing = await this.findExistingReturns({
          code: r.code || "",
          iref: String(r.iref ?? iref),
          date: r.documentDate || "",
        });

        this.setState((s) => ({
          returnDlg: { ...s.returnDlg, existingReturns: existing },
        }));
      } catch (_) {}
    } catch (e) {
      safeDone({ err: e?.message || "Load failed." });
    }
  };

  findExistingReturns = async (sourceOrder) => {
    const { client } = this.state;
    const ref = sourceOrder?.code || "";
    if (!ref) return [];

    let date = sourceOrder?.date || null;
    if (date) {
      date = String(date).trim();
      if (/^\d{2}-\d{2}-\d{4}/.test(date)) {
        const [dd, mm, yyyy] = date.split("-");
        date = `${yyyy}-${mm}-${dd}`;
      } else if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
        date = date.slice(0, 10);
      } else {
        const d = new Date(date);
        date = isNaN(d) ? null : d.toISOString().slice(0, 10);
      }
    }

    const q = {
      documentDate__gte: date,
      description: `Return for order ${ref}`,
      // limit: 1,
    };

    try {
      const resp = await Promise.resolve(client.getData(Config.API_URL_ORDERS, q));
      const list = Array.isArray(resp) ? resp : (resp.orders || []);
      // Filter out the original order if it ever matched by mistake
      return list.filter(o => String(o.description || "").includes(`Return for order ${ref}`));
    } catch {
      return [];
    }
  };

  extractApiError = (resOrErr) => {
    try {
      const r = resOrErr?.response || resOrErr; // in case some client wraps it
      if (r?.status === "Error" || r?.status === "error") {
        const parts = [];
        if (r.code) parts.push(String(r.code));
        if (r.message) parts.push(String(r.message));
        const d = r.data;
        const dataMsg =
          (d && (d.message || d.detail || d.error || d.reason)) || null;
        if (dataMsg) parts.push(String(dataMsg));
        return parts.join(" — ");
      }
      // If it's a thrown Error
      if (r instanceof Error && r.message) return r.message;
      // Some backends put errors directly in data.*
      const d = r?.data;
      if (d && (d.message || d.detail || d.error)) {
        return String(d.message || d.detail || d.error);
      }
    } catch (_) {}
    return null;
  };

  openDetails = (order) => {
    // Debug: check missing ids vs dictionaries
    try {
      const o = order?.raw || order;
      const prodMap = this.state.productNameById || {};
      const comboMap = this.state.comboNameById || {};
      const needProd = new Set();
      const needCombo = new Set();
      [
        ...(o?.orderProductEntry_order || []),
        ...(o?.orderProductCompanyEntry_order || []),
      ].forEach(p => {
        const pid = this.getProductIref(p);
        if (pid && !prodMap[pid]) needProd.add(pid);
      });
      [
        ...(o?.orderComboEntry_order || []),
        ...(o?.orderComboCompanyEntry_order || []),
      ].forEach(c => {
        const cid = this.getComboIref(c);
        if (cid && !comboMap[cid]) needCombo.add(cid);
      });
      (o?.orderProductPromotionEntry_order || []).forEach(pp => {
        const pid = String(pp?.ProductPromotion?.product ?? pp?.product ?? "");
        if (pid && !prodMap[pid]) needProd.add(pid);
      });
      (o?.orderComboPromotionEntry_order || []).forEach(cp => {
        const cid = String(cp?.ComboPromotion?.productCombo ?? cp?.productCombo ?? "");
        if (cid && !comboMap[cid]) needCombo.add(cid);
      });
      if (needProd.size || needCombo.size) {
        console.warn("[Details] Missing in catalogs -> products:", Array.from(needProd), "combos:", Array.from(needCombo));
      }
    } catch(e) {}
    this.setState({ detailsDlg: { open: true, order } });
  };
  closeDetails = () => this.setState({ detailsDlg: { open: false, order: null } });

  renderProductsCell = (o) => (
    <Stack direction="row" flexWrap="wrap">
      {o.items.map((it) => (
        <Chip key={it.key} size="small" label={it.label} color={it.color} icon={<ShoppingBag fontSize="inherit" />} sx={{ mr: 0.5, mb: 0.5 }} />
      ))}
    </Stack>
  );

  fillReturnAll = () => {
    this.setState((s) => {
      const sel = {};
      (s.returnDlg.lines || []).forEach((ln) => {
        sel[ln.key] = Math.max(0, Number(ln.qty || 0));
      });
      return { returnDlg: { ...s.returnDlg, select: sel } };
    });
  };
  clearReturnSelection = () => {
    this.setState((s) => ({ returnDlg: { ...s.returnDlg, select: {} } }));
  };

  buildReturnPayload = () => {
    const { returnDlg } = this.state;
    const { sourceOrder, lines, select, payType } = returnDlg;

    const chosen = lines
      .map((ln) => {
        const q = Number(select[ln.key] || 0);
        return q > 0 ? { ...ln, retQty: q } : null;
      })
      .filter(Boolean);

    const orderProductEntry_order = [];
    const orderComboEntry_order = [];
    const orderProductCompanyEntry_order = [];
    const orderComboCompanyEntry_order = [];
    const orderProductPromotionEntry_order = [];
    const orderComboPromotionEntry_order = [];

    const isFullReturn = chosen.length === 0;

    if (isFullReturn) {
      return {
        orderIref: Number(sourceOrder?.iref),
        payType: Number(payType || 1),
        createdOn: new Date().toISOString(),
      };
    }

    chosen.forEach((ln) => {
      const origQty = Math.abs(Number(ln.qty || 0)) || 1;
      const unitVal = Number(ln.value || 0) / origQty;
      const unitVat = Number(ln.vat || 0) / origQty;

      const max = Math.max(0, Number(ln.qty || 0));
      const retQty = Math.min(Math.abs(Number(ln.retQty)), max);

      const q = retQty;
      const lineVal = Math.round(unitVal * q * 1000000) / 1000000;
      const lineVat = Math.round(unitVat * q * 1000000) / 1000000;

      const common = { value: lineVal, quantity: q, vat: lineVat };
      const usePromo = !!ln.promotionList;

      if (ln.product && !ln.combo) {
        if (usePromo) {
          orderProductPromotionEntry_order.push({
            ...common,
            ProductPromotion: {
              promotionList: ln.promotionList,
              product: ln.product,
            },
          });
        } else if (ln.type === "productCompany") {
          orderProductCompanyEntry_order.push({
            ...common,
            ProductCompany: {
              priceList: ln.priceList,
              product: ln.product,
            },
          });
        } else {
          orderProductEntry_order.push({
            ...common,
            ProductPrice: {
              priceList: ln.priceList,
              product: ln.product,
            },
          });
        }
      } else if (ln.combo) {
        if (usePromo) {
          const children = (ln.comboChildren || []).map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            quantity: retQty * (Number(ch.unitQty) || 0),
            value: Math.round(retQty * (Number(ch.unitVal) || 0) * 1000000) / 1000000,
            vat: Math.round(retQty * (Number(ch.unitVat) || 0) * 1000000) / 1000000,
          }));

          orderComboPromotionEntry_order.push({
            ...common,
            ComboPromotion: {
              productCombo: ln.combo,
              promotionList: ln.promotionList,
            },
            OrderComboPromotionProductsEntry_OrderComboPromotionEntry: children,
          });
        } else if (ln.type === "comboCompany") {
          const children = (ln.comboChildren || []).map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            quantity: retQty * (Number(ch.unitQty) || 0),
            value: Math.round(retQty * (Number(ch.unitVal) || 0) * 1000000) / 1000000,
            vat: Math.round(retQty * (Number(ch.unitVat) || 0) * 1000000) / 1000000,
          }));

          orderComboCompanyEntry_order.push({
            ...common,
            ComboCompany: {
              priceList: ln.priceList,
              productCombo: ln.combo,
            },
            OrderComboCompanyProductsEntry_OrderComboCompanyEntry: children,
          });
        } else {
          const children = (ln.comboChildren || []).map((ch) => ({
            product: ch.product,
            priceType: ch.priceType,
            priceList: ch.priceList ?? null,
            quantity: retQty * (Number(ch.unitQty) || 0),
            value: Math.round(retQty * (Number(ch.unitVal) || 0) * 1000000) / 1000000,
            vat: Math.round(retQty * (Number(ch.unitVat) || 0) * 1000000) / 1000000,
          }));

          orderComboEntry_order.push({
            ...common,
            ComboPrice: {
              productCombo: ln.combo,
              priceList: ln.priceList,
            },
            OrderComboProductsEntry_OrderComboEntry: children,
          });
        }
      }
    });

    return {
      orderIref: Number(sourceOrder?.iref),
      payType: Number(payType || 1),
      createdOn: new Date().toISOString(),
      orderProductEntry_order,
      orderComboEntry_order,
      orderProductCompanyEntry_order,
      orderComboCompanyEntry_order,
      orderProductPromotionEntry_order,
      orderComboPromotionEntry_order,
    };
  };

  submitReturn = async () => {
    if (this.state.returnDlg.existingReturns?.length > 0) {
      this.setState((s) => ({
        returnDlg: { ...s.returnDlg, err: "A return already exists for this order." },
      }));
      return;
    }

    try {
      this.setState((s) => ({
        returnDlg: { ...s.returnDlg, busy: true, err: "" },
      }));

      const payload = this.buildReturnPayload();
      const url = `${Config.API_URL_ORDERS}returnOrder/`;

      const res = this.state.client.pushJson
        ? await this.state.client.pushJson(url, payload)
        : await this.state.client.pushData(url, payload);

      if (res?.status === "Error" || res?.status === "error") {
        const msg = this.extractApiError(res) || `Error: ${res?.code || "RETURN-FAIL"}`;
        throw new Error(msg);
      }

      this.toast(res?.message || "Return order created");

      this.setState((s) => ({
        returnDlg: {
          ...s.returnDlg,
          busy: false,
          err: "",
          step: 2,
          success: res,
        },
      }));

      if (this.props.onSuccess) this.props.onSuccess(res);
    } catch (e) {
      const msg = this.extractApiError(e) || e?.message || "Submit failed.";
      this.setState((s) => ({
        returnDlg: { ...s.returnDlg, busy: false, err: msg },
      }));
    }
  };

  componentDidMount() {
    this.unmounted = false;
    this.bootstrap();
  }

  bootstrap = async () => {
    try {
      this.setState({ loading: true, error: "" });

      // 1) Fetch dictionaries and build { iref -> name } maps
      await this.loadDictionaries();
      if (this.unmounted) return;

      // 2) Fetch orders
      await this.loadOrders();
    } catch (e) {
      if (!this.unmounted) this.setState({ error: e?.message || "Init failed" });
    } finally {
      if (!this.unmounted) this.setState({ loading: false });
    }
  };

  componentWillUnmount() {
    this.unmounted = true;
    this.ordersRequestSequence += 1;
    this.deliveryOrdersRequestSequence += 1;
    this.courierRequestSequence += 1;
    this.deliveryDetailsRequestSequence += 1;
    this.deliveryLookupSequence += 1;
    this.ordersRequest?.abort?.();
    this.deliveryOrdersRequest?.abort?.();
  }

  // ----------------- Render -----------------
  render() {
    const today = getBusinessDate();
    const latestDate = shiftIsoDate(today, FUTURE_ORDER_DAYS);
    const {
      orders,
      loading,
      ordersLoading,
      ordersLastLoadedAt,
      error,
      deliveryOrders,
      deliveryOrdersLoading,
      deliveryOrdersLastLoadedAt,
      deliveryOrdersError,
      deliveryLookup,
      snack,
      acceptDlg,
      cancelDlg,
      courierDlg,
      deliveryDetailsDlg,
      selectedStatus,
      selectedDate,
      detailsDlg,
      viewMode,
    } = this.state;
    const showingDeliveryOrders = viewMode === "deliveryOrders";
    const displayedOrders = showingDeliveryOrders ? deliveryOrders : orders;
    const activeLoading = showingDeliveryOrders ? deliveryOrdersLoading : loading || ordersLoading;
    const displayedLoading = activeLoading && displayedOrders.length === 0;
    const backgroundLoading = activeLoading && displayedOrders.length > 0;
    const displayedError = showingDeliveryOrders ? deliveryOrdersError : error;
    const incidentWait = incidentWaitLabel(
      courierDlg.incident?.closeEligibleOn,
      courierDlg.incident?.serverNow
    );
    const countsByStatus = orders.reduce((acc, o) => {
      acc[o.status] = (acc[o.status] || 0) + 1;
      return acc;
    }, {});

    if (!this.state.catalogsLoaded) {
      return (
        <Box sx={{ minHeight: "80v  h", maxHeight: "90vh", display: "grid", placeItems: "center" }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box 
        sx={{ 
          height: "90vh", bgcolor: "background.default",
          width: {
            xs: 'calc(100vw - 8px)',
            sm: '100%',
          },
          maxWidth: {
            xs: 'calc(100vw - 8px)',
            sm: '100%',
          },
          ml: {
            xs: 'calc(50% - 50vw + 4px)',
            sm: 0,
          },   
        }}
      >

        <Container
          maxWidth="xl"
          sx={{
            py: 3,
            height: viewMode !== "returns" ? '100%' : undefined,
            maxHeight: viewMode === "returns" ? 'calc(90vh - 64px)' : undefined,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              mb: 2,
              p: 2,
              position: "sticky",
              top: (t) => t.mixins.toolbar?.minHeight ?? 0,
              zIndex: (t) => t.zIndex.appBar + 1,
              bgcolor: "background.paper",
              flexShrink: 0,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              flexWrap="wrap"
              rowGap={1}
            >
              {/* LEFT: order-list views share the existing promised date */}
              {viewMode !== "returns" && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Button
                    variant={viewMode === "orders" && selectedStatus === 5 ? "contained" : "outlined"}
                    size="small"
                    onClick={() => this.handleStatusClick(5)}
                  >
                    Paid ({countsByStatus[5] || ""})
                  </Button>
                  <Button
                    variant={viewMode === "orders" && selectedStatus === 6 ? "contained" : "outlined"}
                    size="small"
                    onClick={() => this.handleStatusClick(6)}
                  >
                    Accepted ({countsByStatus[6] || ""})
                  </Button>
                  <Button
                    variant={showingDeliveryOrders ? "contained" : "outlined"}
                    size="small"
                    onClick={this.openDeliveryOrdersView}
                  >
                    Delivery orders ({deliveryOrders.length})
                  </Button>

                  <Stack
                    data-testid="orderhub-date-navigation"
                    direction="row"
                    spacing={0.25}
                    alignItems="center"
                    sx={{ ml: { xs: 0, sm: 1 }, flexWrap: "nowrap", minWidth: 0, '& > *': { flexShrink: 0 } }}
                  >
                    <Tooltip title="Previous day">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Previous day"
                          disabled={selectedDate <= MIN_ORDER_DATE}
                          onClick={() => this.moveDate(-1)}
                        >
                          <NavigateBefore />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <TextField
                      type="date"
                      label="Promised date"
                      size="small"
                      value={selectedDate}
                      onChange={this.handleDateChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: MIN_ORDER_DATE, max: latestDate }}
                      sx={{ width: { xs: 160, sm: 165 } }}
                    />
                    <Tooltip title="Next day">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Next day"
                          disabled={selectedDate >= latestDate}
                          onClick={() => this.moveDate(1)}
                        >
                          <NavigateNext />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Go to today">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Go to today"
                          disabled={selectedDate === today}
                          onClick={() => this.setSelectedDate(today)}
                        >
                          <CalendarToday fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>

                  {showingDeliveryOrders && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        label="Find order #"
                        size="small"
                        value={deliveryLookup.iref}
                        error={Boolean(deliveryLookup.error)}
                        helperText={deliveryLookup.error || undefined}
                        inputProps={{ inputMode: "numeric" }}
                        onChange={(event) => {
                          this.deliveryLookupSequence += 1;
                          const iref = event.target.value;
                          this.setState({ deliveryLookup: { iref, loading: false, error: "" } });
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") this.findDeliveryOrder();
                        }}
                        sx={{ width: { xs: 140, sm: 160 } }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={deliveryLookup.loading}
                        onClick={this.findDeliveryOrder}
                      >
                        {deliveryLookup.loading ? "Finding…" : "Find"}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              )}

              {/* RIGHT: title + refresh or toggle */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Box>
                  <Typography variant="h6" noWrap>
                    {viewMode === "returns"
                      ? "Register return"
                      : showingDeliveryOrders
                        ? "Delivery orders"
                        : "Orders Dashboard"}
                  </Typography>
                  {viewMode !== "returns" && (
                    <AutoRefreshStatus
                      key={viewMode}
                      lastLoadedAt={showingDeliveryOrders ? deliveryOrdersLastLoadedAt : ordersLastLoadedAt}
                      busy={activeLoading}
                      paused={acceptDlg.open || cancelDlg.open || courierDlg.open || detailsDlg.open || deliveryDetailsDlg.open}
                      onRefresh={this.refreshActiveView}
                    />
                  )}
                </Box>

                {viewMode !== "returns" ? (
                  <IconButton aria-label="Refresh orders" onClick={this.refreshActiveView}>
                    <Refresh />
                  </IconButton>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={this.backToOrdersView}
                  >
                    Back to orders
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>

          {/* ===== Views ===== */}
          {viewMode !== "returns" ? (
            // ===== ORDER LIST TABLE VIEW =====
            <Paper variant="outlined" sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              {backgroundLoading && <LinearProgress aria-label="Refreshing orders" />}
              <Box sx={{
                p: 2,
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                opacity: backgroundLoading ? 0.55 : 1,
                pointerEvents: backgroundLoading ? "none" : "auto",
              }}>
                {displayedLoading ? (
                  <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                ) : displayedError ? (
                  <Alert severity="error">{displayedError}</Alert>
                ) : (
                  <TableContainer sx={{ p: 2, flex: 1, minHeight: 0, overflow: 'auto' }} >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {showingDeliveryOrders ? "Delivery contact" : "Client"}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Products</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Items</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                          {showingDeliveryOrders && (
                            <TableCell sx={{ fontWeight: 600 }}>Delivery progress</TableCell>
                          )}
                          <TableCell sx={{ fontWeight: 600 }}>
                            {showingDeliveryOrders ? "Order status" : "Status"}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayedOrders.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={showingDeliveryOrders ? 10 : 9} align="center">
                              {showingDeliveryOrders ? "No delivery orders" : "No orders"}
                            </TableCell>
                          </TableRow>
                        ) : (
                          displayedOrders.map((o) => (
                            <TableRow key={o.iref} hover>
                              <TableCell>{o.locationName}</TableCell>
                              <TableCell>{o.created}</TableCell>
                              <TableCell>
                                <Typography variant="subtitle2">#{o.number}</Typography>
                              </TableCell>
                              <TableCell>{o.customerName}</TableCell>
                              <TableCell>{this.renderProductsCell(o)}</TableCell>
                              <TableCell align="right">{o.itemsCount}</TableCell>
                              <TableCell align="right">{Number(o.total || 0).toFixed(2)}</TableCell>
                              {showingDeliveryOrders && (
                                <TableCell>{this.getDeliveryProgressLabel(o.deliveryStatus)}</TableCell>
                              )}
                              <TableCell><StatusChip status={o.status} /></TableCell>
                              <TableCell align="right">
                                {showingDeliveryOrders ? (
                                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      disabled={backgroundLoading}
                                      onClick={() => this.openDetails(o)}
                                    >
                                      Details
                                    </Button>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      disabled={backgroundLoading}
                                      onClick={() => this.openCourierDialog(o)}
                                    >
                                      Courier
                                    </Button>
                                  </Stack>
                                ) : (
                                  <Actions
                                    row={o}
                                    onAccept={this.onAccept}
                                    onCancel={this.onCancel}
                                    onComplete={this.onComplete}
                                    onDetails={this.openDetails}
                                    disabled={backgroundLoading}
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
              {!showingDeliveryOrders && (
                <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={this.openReturnView}
                    sx={{ color: "text.secondary", textTransform: "none" }}
                  >
                    Register return
                  </Button>
                </Box>
              )}
            </Paper>
          ) : (
            // ===== RETURN VIEW =====
            <Paper
                variant="outlined"
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: "background.paper",
                  maxHeight: 'calc(92vh - 40px)',
                  overflow: 'hidden'
                }}
              >
              {/* Step 0: Find Order by iref */}
              {this.state.returnDlg.step === 0 && (
                <Stack spacing={1.5} sx={{ height: '100%', minHeight: 0 }}>
                  {this.state.returnDlg.err ? (
                    <Alert severity="error">{this.state.returnDlg.err}</Alert>
                  ) : null}

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <TextField
                      label="Order iref"
                      size="small"
                      value={this.state.returnDlg.searchIref}
                      onChange={(e)=>this.setState(s=>({
                        returnDlg:{ ...s.returnDlg, searchIref: e.target.value.replace(/\D/g,"") }
                      }))}
                      onKeyDown={(e)=>{ if(e.key==='Enter'){ this.loadReturnSourceByIref(); } }}
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      disabled={this.state.returnDlg.busy}
                    />
                    <Button
                      variant="contained"
                      onClick={this.loadReturnSourceByIref}
                      disabled={!this.state.returnDlg.searchIref || this.state.returnDlg.busy}
                    >
                      {this.state.returnDlg.busy ? "Loading..." : "Load order"}
                    </Button>
                    <Button size="small" onClick={this.backToOrdersView}>
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              )}

              {/* Step 1: Select Lines to return */}
              {Array.isArray(this.state.returnDlg.existingReturns) && this.state.returnDlg.existingReturns.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  This order already has {this.state.returnDlg.existingReturns.length} return(s).
                  {this.state.returnDlg.existingReturns.slice(0,3).map((o, i) => (
                    <span key={i}> #{o.docNumber || o.iref} — {o.createdOn} — total {Number(o.totalAmount || 0).toFixed(2)};&nbsp;</span>
                  ))}
                </Alert>
              )}
              {this.state.returnDlg.step === 1 && (
                <Stack spacing={1.5}>
                  {this.state.returnDlg.err ? (
                    <Alert severity="error">{this.state.returnDlg.err}</Alert>
                  ) : null}

                  <Stack
                    sx={{
                      px: 1,
                      py: 0.75,
                      bgcolor: (t) => t.palette.action.hover,
                      borderRadius: 1,
                    }}
                    spacing={0.5}
                  >
                    {/* First row */}
                    <Stack
                      direction="row"
                      spacing={1}
                      divider={<Divider orientation="vertical" flexItem />}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      {[
                        {
                          label: "Order",
                          value: this.state.returnDlg.sourceOrder?.code
                            ? `${this.state.returnDlg.sourceOrder.code}${
                                this.state.returnDlg.sourceOrder?.iref
                                  ? ` (${this.state.returnDlg.sourceOrder.iref})`
                                  : ""
                              }`
                            : "-",
                        },
                        { label: "POS", value: this.state.returnDlg.sourceOrder?.pos?.name || "-" },
                        { label: "Date", value: this.state.returnDlg.sourceOrder?.date || "-" },
                      ].map((item, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: "inline-flex",
                            alignItems: "baseline",
                            gap: 0.5,
                            py: 0.25,
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                            {item.label}:
                          </Typography>
                          <Typography variant="body2">{item.value}</Typography>
                        </Box>
                      ))}
                    </Stack>

                    {/* Second row */}
                    <Stack
                      direction="row"
                      spacing={1}
                      divider={<Divider orientation="vertical" flexItem />}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      {[
                        {
                          label: "Total",
                          value: `${Number(this.state.returnDlg.sourceOrder?.valueWVat || 0).toFixed(2)}`,
                        },
                        {
                          label: "Discount included",
                          value: this.state.returnDlg.sourceOrder?.discountValue
                            ? `${Number(this.state.returnDlg.sourceOrder.discountValue).toFixed(2)}`
                            : "0.00",
                        },
                        {
                          label: "Lines",
                          value: this.state.returnDlg.lines.length,
                        },
                      ].map((item, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: "inline-flex",
                            alignItems: "baseline",
                            gap: 0.5,
                            py: 0.25,
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                            {item.label}:
                          </Typography>
                          <Typography variant="body2">{item.value}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Stack>

                  <Divider />

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 1, mb: 1 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Select products to return
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={this.fillReturnAll}
                        sx={{ textTransform: "none", fontWeight: 500 }}
                      >
                        Return all
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={this.clearReturnSelection}
                        sx={{ textTransform: "none", fontWeight: 500 }}
                      >
                        Clear
                      </Button>
                    </Stack>
                  </Stack>

                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <TableContainer sx={{ maxHeight: 320, overflow: "auto" }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Line</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">Qty (orig)</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">Line value (orig)</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">Discount (orig)</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">Return qty</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {this.state.returnDlg.lines.map(ln => {
                            const orig = Number(ln.qty || 0);
                            const max = Math.max(0, orig); // cannot return more than positive qty
                            const cur = Number(this.state.returnDlg.select[ln.key] || 0);
                            return (
                              <TableRow key={ln.key}>
                                <TableCell>{ln.label}</TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{ color: orig < 0 ? 'error.main' : 'text.primary', fontWeight: 500 }}
                                  >
                                    {orig}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">{Number(ln.value || 0).toFixed(2)}</TableCell>
                                <TableCell align="right">{Number(ln.discountValue || 0).toFixed(2)}</TableCell>
                                <TableCell align="right" width={180}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    inputProps={{ min: 0, max }}
                                    value={cur}
                                    onChange={(e)=>{
                                      const v = Math.max(0, Math.min(max, Number(e.target.value || 0)));
                                      this.setState(s=>({
                                        returnDlg:{ ...s.returnDlg, select: { ...s.returnDlg.select, [ln.key]: v } }
                                      }));
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Live totals preview */}
                  {(() => {
                    const { lines, select } = this.state.returnDlg;
                    let subGross = 0, subVat = 0, discGross = 0, discNet = 0;
                    lines.forEach(ln => {
                      const origQty = Math.abs(Number(ln.qty || 0)) || 1;
                      const unitVal = Number(ln.value || 0) / origQty; // gross/unit
                      const unitVat = Number(ln.vat || 0) / origQty;   // vat/unit
                      const q = -Math.abs(Number(select[ln.key] || 0)); // negative for return
                      subGross += unitVal * q;
                      subVat   += unitVat * q;
                      // discount accumulators (proportional to returned qty)
                      const unitDiscGross = Number(ln.discountValue || 0) / origQty;
                      if (unitDiscGross) {
                        const vatShareInGross = unitVal ? (unitVat / unitVal) : 0;
                        const unitDiscNet = unitDiscGross * (1 - vatShareInGross);
                        const retQty = Math.abs(Number(select[ln.key] || 0));
                        discGross += retQty * unitDiscGross;
                        discNet   += retQty * unitDiscNet;
                      }
                    });
                    const subNet = subGross - subVat;
                    const totalGross = subGross + discGross;
                    const totalNet   = subNet   + discNet;
                    return (
                      <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { width: '100%' } }}>
                        <Typography variant="body2">
                          Subtotal (gross): <b>{subGross.toFixed(2)}</b> · Discount (gross): <b>{-discGross.toFixed(2)}</b> · Total (gross): <b>{totalGross.toFixed(2)}</b>
                        </Typography>
                        <Typography variant="body2">
                          Subtotal (net): <b>{subNet.toFixed(2)}</b> · Discount (net): <b>{-discNet.toFixed(2)}</b> · Total (net): <b>{totalNet.toFixed(2)}</b> · Payment: <b>{this.state.returnDlg.payType} ({this.state.returnDlg.payType === 1 ? "Cash" : "Bank Transfer"})</b> · Amount: <b>{totalGross.toFixed(2)}</b>
                        </Typography>
                      </Alert>
                    );
                  })()}

                  <Divider sx={{ mt: 2, mb: 2 }} />

                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      mt: 1,
                      position: "sticky",
                      bottom: 0,
                      bgcolor: "background.paper",
                      pt: 1,
                      borderTop: (t) => `1px solid ${t.palette.divider}`,
                      zIndex: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() =>
                        this.setState((s) => ({ returnDlg: { ...s.returnDlg, step: 0 } }))
                      }
                      sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                      ← Back
                    </Button>

                    <TextField
                      select
                      label="Payment"
                      size="small"
                      value={this.state.returnDlg.payType}
                      onChange={(e) =>
                        this.setState((s) => ({
                          returnDlg: { ...s.returnDlg, payType: Number(e.target.value) }
                        }))
                      }
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 180 }}
                    >
                      <option value={1}>Cash</option>
                      <option value={2}>Bank Transfer</option>
                    </TextField>

                    <Button
                      variant="contained"
                      color="success"
                      onClick={this.submitReturn}
                      disabled={this.state.returnDlg.busy || (this.state.returnDlg.existingReturns?.length > 0)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        minWidth: 140,
                      }}
                    >
                      {this.state.returnDlg.busy ? "Submitting…" : "Create return"}
                    </Button>

                    <Button
                      variant="text"
                      color="inherit"
                      onClick={this.backToOrdersView}
                      sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                      Close
                    </Button>
                  </Stack>

                </Stack>
              )}
            </Paper>
          )}

          {/* ===== Details dialog (unchanged) ===== */}
          <Dialog open={detailsDlg.open} onClose={this.closeDetails} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ pb: 1.5 }}>
              <Box display="flex" flexWrap="wrap" alignItems="baseline" gap={1}>
                <Typography variant="h6" component="span" sx={{ mr: 0.5 }}>
                  Order details #{detailsDlg.order?.number || detailsDlg.order?.iref}
                </Typography>
                {detailsDlg.order?.raw?.pos?.name && (
                  <Typography variant="subtitle2" color="text.secondary" component="span">
                    — {detailsDlg.order.raw.pos.name}
                  </Typography>
                )}
              </Box>

              {(detailsDlg.order?.raw?.createdOn ||
                detailsDlg.order?.raw?.scheduledOn ||
                detailsDlg.order?.raw?.totalAmount) && (
                <Stack
                  direction="row"
                  spacing={2}
                  divider={<Divider orientation="vertical" flexItem />}
                  sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}
                >
                  {detailsDlg.order?.raw?.createdOn && (
                    <Typography variant="body2" color="text.secondary">
                      <Box component="span" sx={{ fontWeight: 600 }}>Created:</Box>{' '}
                      {detailsDlg.order.raw.createdOn}
                    </Typography>
                  )}
                  {detailsDlg.order?.raw?.scheduledOn && (
                    <Typography variant="body2" color="text.secondary">
                      <Box component="span" sx={{ fontWeight: 600 }}>Scheduled:</Box>{' '}
                      {detailsDlg.order.raw.scheduledOn}
                    </Typography>
                  )}
                  {detailsDlg.order?.raw?.totalAmount && (
                    <Typography variant="body2" color="text.secondary">
                      <Box component="span" sx={{ fontWeight: 600 }}>Total:</Box>{' '}
                      {Number(detailsDlg.order.raw.totalAmount || 0).toFixed(2)}
                    </Typography>
                  )}
                </Stack>
              )}

              {detailsDlg.order?.raw?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>Notes:</Box>{' '}
                  {detailsDlg.order.raw.description}
                </Typography>
              )}
            </DialogTitle>

            <DialogContent
              dividers
              sx={{
                maxHeight: '70vh',
                overflowY: 'auto',
                pt: 2
              }}
            >
              {(() => {
                const o = detailsDlg.order?.raw;
                if (!o) return <Typography variant="body2">No data</Typography>;

                const prods = [
                  ...(o.orderProductEntry_order || []),
                  ...(o.orderProductCompanyEntry_order || []),
                ];
                const combos = [
                  ...(o.orderComboEntry_order || []),
                  ...(o.orderComboCompanyEntry_order || []),
                ];
                const pPromos = o.orderProductPromotionEntry_order || [];
                const cPromos = o.orderComboPromotionEntry_order || [];

                const { delivery, address } = this.getDeliveryInfo(o);
                const latitude = String(address?.gps_lt ?? "").trim();
                const longitude = String(address?.gps_lg ?? "").trim();
                const mapLink = latitude && longitude
                  && Math.abs(Number(latitude)) <= 90 && Math.abs(Number(longitude)) <= 180
                  ? destinationMapLink({ latitude: Number(latitude), longitude: Number(longitude) })
                  : "";

                const net = Number(o.totalAmount || 0);
                const discount = Number(o.discountValue || 0);
                const total = net + discount;

                return (
                  <Stack spacing={2}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: "background.paper",
                        width: "100%",
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                          flexWrap="wrap"
                        >
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              Delivery details
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {this.getDeliveryTypeLabel(o?.deliveryType)}
                              {delivery?.expectedDelivery ? ` · Expected: ${delivery.expectedDelivery}` : ""}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={`Pickup code: ${this.getPickupCode(o, delivery)}`}
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                          {Number(o.deliveryType) === 1 && (
                            <Button size="small" variant="outlined"
                              onClick={() => this.openDeliveryDetails(detailsDlg.order)}>
                              {delivery ? "Edit delivery details" : "Add delivery details"}
                            </Button>
                          )}
                        </Stack>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "1fr 1fr",
                              md: "1.2fr 1fr 1fr",
                            },
                            gap: 1.25,
                          }}
                        >
                          <this.InfoLine
                            label={o?.deliveryType === 1 ? "Delivery contact" : "Client"}
                            value={this.getClientName(o)}
                          />

                          <this.InfoLine
                            label="Delivery phone"
                            value={this.getDeliveryPhone(delivery, o)}
                          />

                          <this.InfoLine
                            label="Order status"
                            value={this.getOrderStatusLabel(o?.status)}
                          />

                          <this.InfoLine
                            label="Delivery progress"
                            value={this.getDeliveryProgressLabel(o?.deliveryStatus)}
                          />

                          <this.InfoLine
                            label="Delivery platform status"
                            value={delivery
                              ? this.getDeliveryPlatformStatusLabel(delivery.status)
                              : "Not available"}
                          />
                        </Box>

                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            bgcolor: (t) => t.palette.action.hover,
                            border: (t) => `1px solid ${t.palette.divider}`,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}
                          >
                            Delivery address
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.25,
                              fontWeight: 500,
                              lineHeight: 1.5,
                              wordBreak: "break-word",
                            }}
                          >
                            {this.formatAddress(address)}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Coordinates: {mapLink ? (
                              <>
                                {latitude}, {longitude}{" · "}
                                <Link href={mapLink} target="_blank" rel="noopener noreferrer">
                                  Open in Google Maps
                                </Link>
                              </>
                            ) : "Unavailable"}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "1fr 1fr",
                              md: "1fr 1fr 1fr",
                            },
                            gap: 1.25,
                          }}
                        >
                          <this.InfoLine
                            label="Delivery type"
                            value={this.getDeliveryTypeLabel(o?.deliveryType)}
                          />

                          <this.InfoLine
                            label="Expected delivery"
                            value={delivery?.expectedDelivery}
                          />

                          <this.InfoLine
                            label="Pickup code"
                            value={this.getPickupCode(o, delivery)}
                          />

                          <this.InfoLine
                            label="Tracking number"
                            value={delivery?.trackingNumber}
                          />

                          <this.InfoLine
                            label="Delivery fee"
                            value={`${Number(delivery?.fee || 0).toFixed(2)}`}
                          />

                          <this.InfoLine
                            label="Delivery progress"
                            value={this.getDeliveryProgressLabel(o?.deliveryStatus)}
                          />

                          <this.InfoLine
                            label="Delivery platform status"
                            value={delivery
                              ? this.getDeliveryPlatformStatusLabel(delivery.status)
                              : "Not available"}
                          />
                        </Box>

                        {delivery?.details && (
                          <Box
                            sx={{
                              p: 1,
                              borderRadius: 1.25,
                              bgcolor: "background.default",
                              border: (t) => `1px dashed ${t.palette.divider}`,
                            }}
                          >
                            <this.InfoLine
                              label="Delivery notes"
                              value={delivery.details}
                            />
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                    <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                      <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, width: '55%' }}>Item</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: '15%' }} align="right">Qty</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: '15%' }} align="right">Value</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: '15%' }} align="right">VAT</TableCell>
                          </TableRow>
                        </TableHead>
                      </Table>
                    </TableContainer>

                    {prods.length > 0 && (
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Products
                        </Typography>
                        <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableBody>
                              {prods.map((p, idx) => (
                                <TableRow
                                  key={`p_${p.iref ?? this.getProductIref(p)}_${idx}`}
                                  sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                                >
                                  <TableCell sx={{ width: '55%' }}>
                                    {(() => {
                                      const pid = this.getProductIref(p);
                                      return (this.state.productNameById || {})[pid] || pid;
                                    })()}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {p.quantity}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(p.value || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(p.vat || 0).toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {combos.length > 0 && (
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Combos
                        </Typography>
                        <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableBody>
                              {combos.map((c, idx) => (
                                <TableRow
                                  key={`c_${c.iref ?? this.getComboIref(c)}_${idx}`}
                                  sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                                >
                                  <TableCell sx={{ width: '55%' }}>
                                    {(() => {
                                      const cid = this.getComboIref(c);
                                      return (this.state.comboNameById || {})[cid] || cid;
                                    })()}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {c.quantity}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(c.value || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(c.vat || 0).toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {pPromos.length > 0 && (
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Product promotions
                        </Typography>
                        <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableBody>
                              {pPromos.map((pp, idx) => (
                                <TableRow
                                  key={`pp_${pp.iref ?? (pp?.ProductPromotion?.product ?? '')}_${idx}`}
                                  sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                                >
                                  <TableCell sx={{ width: '55%' }}>
                                    {(() => {
                                      const pid = String(pp?.ProductPromotion?.product ?? pp?.product ?? '');
                                      return (this.state.productNameById || {})[pid] || pid;
                                    })()}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {pp.quantity}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(pp.value || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(pp.vat || 0).toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {cPromos.length > 0 && (
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Combo promotions
                        </Typography>
                        <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableBody>
                              {cPromos.map((cp, idx) => (
                                <TableRow
                                  key={`cp_${cp.iref ?? (cp?.ComboPromotion?.productCombo ?? '')}_${idx}`}
                                  sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                                >
                                  <TableCell sx={{ width: '55%' }}>
                                    {(() => {
                                      const cid = String(cp?.ComboPromotion?.productCombo ?? cp?.productCombo ?? '');
                                      return (this.state.comboNameById || {})[cid] || cid;
                                    })()}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {cp.quantity}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(cp.value || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell sx={{ width: '15%' }} align="right">
                                    {Number(cp.vat || 0).toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {(total || discount) ? (
                      <Box
                        display="flex"
                        justifyContent="flex-end"
                        sx={{ mt: 1 }}
                      >
                        <Stack spacing={0.5} sx={{ minWidth: 260 }}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Subtotal (with VAT)</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {total.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Discount (with VAT)</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              − {discount.toFixed(2)}
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 0.5 }} />
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Total (with VAT)</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {net.toFixed(2)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ) : null}
                  </Stack>
                );
              })()}
            </DialogContent>

            <DialogActions>
              <Button onClick={this.closeDetails} variant="contained">Close</Button>
            </DialogActions>
          </Dialog>

          {/* Managed Glovo courier dialog */}
          <Dialog
            open={courierDlg.open}
            onClose={courierDlg.busy ? undefined : this.closeCourierDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Glovo courier for order #{courierDlg.order?.number || courierDlg.order?.iref}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1.25,
                  }}
                >
                  <this.InfoLine
                    label="Order status"
                    value={this.getOrderStatusLabel(courierDlg.order?.status)}
                  />
                  <this.InfoLine
                    label="Delivery progress at last order-list refresh"
                    value={this.getDeliveryProgressLabel(
                      courierDlg.order?.raw?.deliveryStatus ?? courierDlg.order?.deliveryStatus
                    )}
                  />
                </Box>

                {courierDlg.loading ? (
                  <Stack alignItems="center" sx={{ py: 3 }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Checking managed Glovo status…
                    </Typography>
                  </Stack>
                ) : (
                  <>
                    {courierDlg.incident
                      && (courierDlg.incident.closedUndelivered
                        || courierDlg.incident.closeEligibleOn
                        || courierDlg.incident.canRequestReplacement
                        || courierDlg.dispatch?.errorCode === "CANCEL_AFTER_PICKUP_CONFLICT") && (
                      <Alert severity={courierDlg.incident.closedUndelivered ? "success" : "info"}>
                        {courierDlg.incident.closedUndelivered ? (
                          <>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Delivery marked undelivered
                            </Typography>
                            <Typography variant="body2">
                              {courierDlg.incident.closedOn
                                ? `Closed ${this.formatCourierDateTime(courierDlg.incident.closedOn)}. `
                                : ""}
                              Customer support should resolve the failed delivery with the customer.
                              {courierDlg.incident.canRequestReplacement
                                ? " If the customer still wants fresh delivery, HQ can reopen delivery and request another courier for this same order after verifying the saved destination."
                                : " A further courier request is currently unavailable; contact support if delivery is still needed."}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2">
                            {courierDlg.incident.canRequestReplacement
                              ? "HQ may request another courier for freshly prepared products on this same order after confirming with the customer. "
                              : "HQ must review this delivery incident with the customer. "}
                            {courierDlg.incident.canCloseUndelivered
                              ? "HQ can mark it undelivered after confirming the customer received nothing."
                              : courierDlg.incident.closeEligibleOn
                                ? `Undelivered closeout becomes available ${this.formatCourierDateTime(courierDlg.incident.closeEligibleOn)}${incidentWait ? ` (${incidentWait} at last refresh)` : ""}. The server checks the six-hour wait.`
                                : ""}
                          </Typography>
                        )}
                      </Alert>
                    )}

                    {courierDlg.incident?.historicalEvidenceConflict && (
                      <Alert severity="error">
                        An earlier courier attempt reported new delivery evidence after another courier was requested.
                        Another courier may already be active. Contact support immediately to
                        check for duplicate delivery before any further courier request.
                      </Alert>
                    )}

                    {courierDlg.dispatch?.providerStatus === "CANCELLED" && (
                      <Alert severity="warning">
                        Glovo reports this courier attempt as cancelled. Any reason or source
                        below comes from Glovo; confirm the delivery outcome with the customer.
                      </Alert>
                    )}

                    {courierDlg.notFound && (
                      <Alert severity="info">
                        No managed Glovo courier has been requested for this order.
                      </Alert>
                    )}

                    {courierDlg.dispatch && (
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Current courier attempt {courierDlg.dispatch.generation || ""}
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1.25,
                          }}
                        >
                          <this.InfoLine
                            label="Courier lifecycle"
                            value={courierDlg.dispatch.status}
                          />
                          <this.InfoLine
                            label="Glovo status"
                            value={courierDlg.dispatch.providerStatus || "Not reported"}
                          />
                          {courierDlg.dispatch.providerStatus === "CANCELLED" && (
                            <this.InfoLine
                              label="Glovo cancellation reason"
                              value={courierDlg.dispatch.cancellationReasonLabel
                                || courierDlg.dispatch.cancellationReason || "Not reported by Glovo"}
                            />
                          )}
                          {courierDlg.dispatch.cancellationSource && (
                            <this.InfoLine
                              label="Glovo cancellation source"
                              value={courierDlg.dispatch.cancellationSource}
                            />
                          )}
                          <this.InfoLine
                            label="Generation"
                            value={String(courierDlg.dispatch.generation ?? "-")}
                          />
                          <this.InfoLine
                            label="Glovo order ID"
                            value={courierDlg.dispatch.providerOrderId || "Not assigned"}
                          />
                          <this.InfoLine
                            label="Courier name"
                            value={courierDlg.dispatch.courierName || "Not assigned"}
                          />
                          <this.InfoLine
                            label="Courier phone"
                            value={(() => {
                              const phone = courierDlg.dispatch.courierPhone;
                              const phoneLink = this.getCourierPhoneLink(phone);
                              return phoneLink ? (
                                <Link href={phoneLink}>{phone}</Link>
                              ) : "Not assigned";
                            })()}
                          />
                          {courierDlg.dispatch.pickedUpOn && (
                            <this.InfoLine
                              label="Pickup evidence recorded"
                              value={this.formatCourierDateTime(
                                courierDlg.dispatch.pickedUpOn
                              )}
                            />
                          )}
                          <this.InfoLine
                            label="Estimated pickup"
                            value={this.formatCourierDateTime(
                              courierDlg.dispatch.estimatedPickupOn
                            )}
                          />
                          <this.InfoLine
                            label="Estimated delivery"
                            value={this.formatCourierDateTime(
                              courierDlg.dispatch.estimatedDeliveryOn
                            )}
                          />
                          <this.InfoLine
                            label="Tracking"
                            value={(() => {
                              const trackingLink = this.getSafeTrackingLink(
                                courierDlg.dispatch.trackingLink
                              );
                              return trackingLink ? (
                                <Link
                                  href={trackingLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Open Glovo tracking
                                </Link>
                              ) : "Not available";
                            })()}
                          />
                        </Box>
                      </Paper>
                    )}

                    {Array.isArray(courierDlg.incident?.history)
                      && courierDlg.incident.history.some((entry) =>
                        Number(entry.generation) !== Number(courierDlg.dispatch?.generation)) && (
                      <Box component="details">
                        <Typography component="summary" variant="body2" sx={{ cursor: "pointer", fontWeight: 700 }}>
                          Previous courier attempts
                        </Typography>
                        {courierDlg.incident.history
                          .filter((entry) => Number(entry.generation) !== Number(courierDlg.dispatch?.generation))
                          .map((entry) => (
                            <Typography key={entry.iref} variant="body2" sx={{ mt: 0.75 }}>
                              Attempt {entry.generation}: {entry.status} · Glovo {entry.providerStatus || "not reported"}
                              {entry.providerOrderId ? ` · ID ${entry.providerOrderId}` : ""}
                              {entry.cancellationReasonLabel || entry.cancellationReason
                                ? ` · Glovo reason: ${entry.cancellationReasonLabel || entry.cancellationReason}`
                                : ""}
                              {entry.cancellationSource ? ` · Glovo source: ${entry.cancellationSource}` : ""}
                              {entry.closedUndelivered
                                ? ` · closed as undelivered${entry.closedOn ? ` ${this.formatCourierDateTime(entry.closedOn)}` : ""}`
                                : ""}
                              {entry.pickedUpOn
                                ? ` · pickup evidence recorded ${this.formatCourierDateTime(entry.pickedUpOn)}`
                                : ""}
                            </Typography>
                          ))}
                      </Box>
                    )}

                    {courierDlg.incidentActionError && (
                      <Alert severity={courierDlg.unknownAction ? "warning" : "error"}>
                        {courierDlg.incidentActionError}
                      </Alert>
                    )}

                    {(courierDlg.error || courierDlg.dispatch?.errorCode) && (
                      <Alert severity="error">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {courierDlg.errorCode || courierDlg.dispatch?.errorCode || "COURIER_ATTENTION_REQUIRED"}
                        </Typography>
                        <Typography variant="body2">
                          {courierDlg.error || courierDlg.dispatch?.message || "Courier status requires support attention."}
                        </Typography>
                        {(courierDlg.errorCode === "CANCEL_AFTER_PICKUP_CONFLICT"
                          || courierDlg.dispatch?.errorCode === "CANCEL_AFTER_PICKUP_CONFLICT") && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {courierDlg.dispatch?.providerStatus === "CANCELLED"
                              ? "Glovo cancelled this courier attempt, but Kalopsia recorded pickup evidence. "
                              : "Cancellation and pickup evidence conflict in Kalopsia. "}
                            The recorded time is when evidence was observed, not a confirmed
                            handoff time. Confirm with the customer whether anything was received before
                            requesting another courier or marking delivery undelivered.
                          </Typography>
                        )}
                      </Alert>
                    )}

                    {courierDlg.notFound && !this.canCreateCourier() && (
                      <Alert severity="warning">
                        A new courier cannot be requested because delivery progress is
                        {` ${this.getDeliveryProgressLabel(
                          courierDlg.order?.raw?.deliveryStatus ?? courierDlg.order?.deliveryStatus
                        )}`}.
                      </Alert>
                    )}

                    {courierDlg.confirmAction === "create" && (
                      <Alert severity="warning">
                        This sends a real courier request to Glovo. If the result is uncertain,
                        do not retry blindly; refresh the courier status or request support.
                      </Alert>
                    )}

                    {courierDlg.confirmAction === "close" && (
                      <Alert severity="warning">
                        <Typography variant="body2">
                          Mark this delivery undelivered only if the customer received nothing.
                          This updates delivery status and resolves its monitoring alert. Customer
                          support handles any financial follow-up. A later fresh delivery on this
                          same order remains possible after customer confirmation.
                        </Typography>
                        <FormControlLabel
                          control={<Checkbox checked={courierDlg.closeConfirmed} disabled={courierDlg.busy}
                            onChange={(event) => this.setState((state) => ({
                              courierDlg: { ...state.courierDlg, closeConfirmed: event.target.checked },
                            }))} />}
                          label="I confirm this order was not delivered to the customer"
                        />
                      </Alert>
                    )}

                    {courierDlg.confirmAction === "replace" && (
                      <Alert severity="warning">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Request a courier for newly prepared products of this same order
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Saved destination: {courierDlg.incident?.destination?.address || "Unavailable"}
                        </Typography>
                        <Typography variant="body2">
                          Saved phone: {courierDlg.incident?.destination?.phone || "Unavailable"}
                        </Typography>
                        <Typography variant="body2">
                          Saved recipient: {courierDlg.incident?.destination?.recipientName || "Unavailable"}
                        </Typography>
                        <Typography variant="body2">
                          Saved map pin: {destinationMapLink(courierDlg.incident?.destination) ? (
                            <Link href={destinationMapLink(courierDlg.incident.destination)}
                              target="_blank" rel="noopener noreferrer">
                              {courierDlg.incident.destination.latitude}, {courierDlg.incident.destination.longitude} (open map)
                            </Link>
                          ) : "Unavailable"}
                        </Typography>
                        {courierDlg.incident?.destination?.notes && (
                          <Typography variant="body2">
                            Saved delivery notes: {courierDlg.incident.destination.notes}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          If the saved recipient, address, map pin, phone, or delivery notes are wrong, do not confirm or send a courier.
                          Contact support to correct the saved destination before requesting a courier.
                        </Typography>
                        <FormControlLabel
                          control={<Checkbox checked={courierDlg.replacementConfirmed} disabled={courierDlg.busy}
                            onChange={(event) => this.setState((state) => ({
                              courierDlg: { ...state.courierDlg, replacementConfirmed: event.target.checked },
                            }))} />}
                          label="I confirmed the customer received nothing, still wants delivery, and verified the saved recipient, phone, address, map pin, and delivery notes; freshly prepared products for this same order are ready"
                        />
                      </Alert>
                    )}

                    {courierDlg.confirmAction === "cancel" && (
                      <>
                        <Alert severity="warning">
                          Cancel only before pickup. The Backend will reject cancellation when
                          provider or delivery evidence makes it unsafe.
                        </Alert>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Cancellation reason"
                          value={courierDlg.cancelReason}
                          disabled={courierDlg.busy}
                          onChange={(event) => this.setState((state) => ({
                            courierDlg: {
                              ...state.courierDlg,
                              cancelReason: event.target.value,
                            },
                          }))}
                        >
                          {COURIER_CANCEL_REASONS.map((reason) => (
                            <MenuItem key={reason.value} value={reason.value}>
                              {reason.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </>
                    )}
                  </>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ flexWrap: "wrap", rowGap: 1 }}>
              <Button disabled={courierDlg.busy} onClick={this.closeCourierDialog}>
                Close
              </Button>

              {!courierDlg.loading && !courierDlg.confirmAction && (
                <Button
                  disabled={courierDlg.busy}
                  onClick={() => this.loadCourierState()}
                  startIcon={<Refresh />}
                >
                  Refresh courier
                </Button>
              )}

              {!courierDlg.confirmAction && (
                <Button disabled={courierDlg.busy || courierDlg.loading}
                  onClick={() => this.openDeliveryDetails(courierDlg.order)}>
                  Delivery details
                </Button>
              )}

              {!courierDlg.loading && !courierDlg.confirmAction && this.canCreateCourier() && (
                <Button
                  variant="contained"
                  color="success"
                  disabled={courierDlg.busy}
                  onClick={() => this.requestCourierConfirmation("create")}
                >
                  {courierDlg.dispatch?.canRetryAfterCorrection === true
                    ? "Retry Glovo courier"
                    : courierDlg.dispatch?.status === "CANCELLED"
                    ? "Call another Glovo courier"
                    : "Call Glovo courier"}
                </Button>
              )}

              {!courierDlg.loading && !courierDlg.confirmAction && this.canCancelCourier() && (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={courierDlg.busy}
                  onClick={() => this.requestCourierConfirmation("cancel")}
                >
                  Cancel Glovo courier
                </Button>
              )}

              {!courierDlg.loading && !courierDlg.confirmAction && this.canCloseCourierIncident() && (
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={courierDlg.busy}
                  onClick={() => this.requestIncidentConfirmation("close")}
                >
                  Close as undelivered
                </Button>
              )}

              {!courierDlg.loading && !courierDlg.confirmAction && this.canRequestIncidentReplacement() && (
                <Button
                  variant="contained"
                  color="success"
                  disabled={courierDlg.busy}
                  onClick={() => this.requestIncidentConfirmation("replace")}
                >
                  {courierDlg.incident?.closedUndelivered
                    ? "Reopen delivery and request Glovo courier"
                    : "Request another Glovo courier"}
                </Button>
              )}

              {courierDlg.confirmAction && (
                <>
                  <Button
                    disabled={courierDlg.busy}
                    onClick={() => this.setState((state) => ({
                      courierDlg: { ...state.courierDlg, confirmAction: null },
                    }))}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    color={courierDlg.confirmAction === "cancel" ? "error"
                      : courierDlg.confirmAction === "close" ? "warning" : "success"}
                    disabled={courierDlg.busy
                      || (courierDlg.confirmAction === "close" && !courierDlg.closeConfirmed)
                      || (courierDlg.confirmAction === "replace" && !courierDlg.replacementConfirmed)}
                    onClick={["close", "replace"].includes(courierDlg.confirmAction)
                      ? this.confirmIncidentAction : this.confirmCourierAction}
                  >
                    {courierDlg.busy
                      ? "Working…"
                      : courierDlg.confirmAction === "cancel"
                        ? "Confirm courier cancellation"
                        : courierDlg.confirmAction === "close"
                          ? "Confirm undelivered closeout"
                          : courierDlg.confirmAction === "replace"
                            ? "Confirm and request Glovo courier"
                        : "Confirm courier request"}
                  </Button>
                </>
              )}
            </DialogActions>
          </Dialog>

          <Dialog open={deliveryDetailsDlg.open}
            onClose={deliveryDetailsDlg.busy ? undefined : this.closeDeliveryDetails}
            disableEscapeKeyDown={deliveryDetailsDlg.busy} maxWidth="sm" fullWidth>
            <DialogTitle>
              {deliveryDetailsDlg.snapshot?.delivery ? "Edit" : "Add"} delivery details
              {` for order #${deliveryDetailsDlg.order?.number || deliveryDetailsDlg.order?.iref || ""}`}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                {deliveryDetailsDlg.loading && <CircularProgress size={24} aria-label="Loading delivery details" />}
                {deliveryDetailsDlg.error && <Alert severity="error">{deliveryDetailsDlg.error}</Alert>}
                {!deliveryDetailsDlg.loading && deliveryDetailsDlg.snapshot && (
                  <>
                    {!deliveryDetailsDlg.snapshot.canEdit && (
                      <Alert severity="warning">
                        {deliveryDetailsDlg.snapshot.editBlockedReason || "Delivery details cannot be changed in the current courier state."}
                      </Alert>
                    )}
                    {deliveryDetailsDlg.snapshot.addresses.length === 0 && (
                      <Alert severity="warning">No saved delivery address is available for this client.</Alert>
                    )}
                    <TextField select fullWidth label="Saved delivery address"
                      value={deliveryDetailsDlg.addressIref}
                      disabled={deliveryDetailsDlg.busy || !deliveryDetailsDlg.snapshot.canEdit || deliveryDetailsDlg.needsRefresh}
                      error={Boolean(deliveryDetailsDlg.fieldErrors.addressIref)}
                      helperText={deliveryDetailsDlg.fieldErrors.addressIref || "Select this client's saved address. This changes only this order's delivery."}
                      onChange={(event) => this.changeDeliveryDetail("addressIref", event.target.value)}>
                      <MenuItem value="">Select a saved address</MenuItem>
                      {deliveryDetailsDlg.snapshot.addresses.map((address) => (
                        <MenuItem key={address.iref} value={String(address.iref)}>{address.label}</MenuItem>
                      ))}
                    </TextField>
                    <TextField fullWidth label="Recipient name" value={deliveryDetailsDlg.recipientName}
                      inputProps={{ maxLength: 255 }}
                      disabled={deliveryDetailsDlg.busy || !deliveryDetailsDlg.snapshot.canEdit || deliveryDetailsDlg.needsRefresh}
                      error={Boolean(deliveryDetailsDlg.fieldErrors.recipientName)}
                      helperText={deliveryDetailsDlg.fieldErrors.recipientName}
                      onChange={(event) => this.changeDeliveryDetail("recipientName", event.target.value)} />
                    <TextField fullWidth label="Delivery phone" value={deliveryDetailsDlg.clientPhone}
                      inputProps={{ inputMode: "tel", maxLength: 32 }}
                      disabled={deliveryDetailsDlg.busy || !deliveryDetailsDlg.snapshot.canEdit || deliveryDetailsDlg.needsRefresh}
                      error={Boolean(deliveryDetailsDlg.fieldErrors.clientPhone)}
                      helperText={deliveryDetailsDlg.fieldErrors.clientPhone || "For example 0722 123 456 or +447700900123. Maximum 13 characters after formatting."}
                      onChange={(event) => this.changeDeliveryDetail("clientPhone", event.target.value)} />
                    <TextField fullWidth multiline minRows={2} label="Courier notes" value={deliveryDetailsDlg.details}
                      inputProps={{ maxLength: 1000 }}
                      disabled={deliveryDetailsDlg.busy || !deliveryDetailsDlg.snapshot.canEdit || deliveryDetailsDlg.needsRefresh}
                      onChange={(event) => this.changeDeliveryDetail("details", event.target.value)} />
                  </>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ flexWrap: "wrap", rowGap: 1 }}>
              <Button disabled={deliveryDetailsDlg.busy} onClick={this.closeDeliveryDetails}>Close</Button>
              <Button disabled={deliveryDetailsDlg.busy || deliveryDetailsDlg.loading}
                onClick={this.loadDeliveryDetails}>Refresh delivery details</Button>
              <Button variant="contained" onClick={this.saveDeliveryDetails}
                disabled={deliveryDetailsDlg.busy || deliveryDetailsDlg.loading || deliveryDetailsDlg.needsRefresh
                  || !deliveryDetailsDlg.snapshot?.canEdit || !deliveryDetailsDlg.snapshot.addresses.length}>
                {deliveryDetailsDlg.busy ? "Saving…" : "Save delivery details"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Accept warning dialog */}
          <Dialog
            open={acceptDlg.open}
            onClose={acceptDlg.busy ? undefined : () => this.setState({
              acceptDlg: { open: false, order: null, busy: false },
            })}
          >
            <DialogTitle>Accept order in Backoffice?</DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mt: 1 }}>
                Accepting order #{acceptDlg.order?.number} changes it from Paid (status 5).
                A POS that fetches only status-5 orders may no longer download it. Continue
                only if the order is already visible or will be handled outside that POS flow.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button
                disabled={acceptDlg.busy}
                onClick={() => this.setState({
                  acceptDlg: { open: false, order: null, busy: false },
                })}
              >
                Keep as paid
              </Button>
              <Button
                color="warning"
                variant="contained"
                disabled={acceptDlg.busy}
                onClick={this.confirmAccept}
                startIcon={<CheckCircle />}
              >
                {acceptDlg.busy ? "Accepting…" : "Accept in Backoffice"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Cancel dialog */}
          <Dialog open={cancelDlg.open} onClose={() => this.setState({ cancelDlg: { open: false, order: null, reason: "" } })}>
            <DialogTitle>Cancel order</DialogTitle>
            <DialogContent>
              <DialogContentText>Reason for cancelling order #{cancelDlg.order?.number}</DialogContentText>
              <TextField fullWidth value={cancelDlg.reason} onChange={(e) => this.setState({ cancelDlg: { ...cancelDlg, reason: e.target.value } })} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.setState({ cancelDlg: { open: false, order: null, reason: "" } })}>Close</Button>
              <Button color="error" variant="contained" onClick={this.confirmCancel} startIcon={<Cancel />}>Cancel order</Button>
            </DialogActions>
          </Dialog>

          <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => this.setState({ snack: { open: false, msg: "", sev: snack.sev } })} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
            <Alert onClose={() => this.setState({ snack: { open: false, msg: "", sev: snack.sev } })} severity={snack.sev} sx={{ width: "100%" }}>{snack.msg}</Alert>
          </Snackbar>
        </Container>
      </Box>
    );
  }
}
